import { useState, useEffect, useRef, useCallback, useMemo } from "react";

import MainContent from '../../components/MainContent/MainContent'
import AppShell from '../../page/AppShell/AppShell'
import ErrorAlert from "../Alerts/ErrorAlert";
import { CategoriesDashFetchAPI } from "../../api/CategoriesDash";
import { ExcelModuleFechtAPI } from "../../api/ExcelModule";
import { GroupsFechtAPI } from "../../api/Groups";
import { ItemsFechtAPI } from "../../api/Items";
import { SegmentsFechtAPI } from "../../api/Segments";
import { TaxesFetchAPI } from "../../api/Taxes";
import { buildCloudinaryImageUrl } from "../utils/CloudinaryImageUrl";
import ProductTour from "./ProductTour/ProductTour";
import { TranslationsButton } from "./Translations/TranslationsPanel";
import "./ProductPanel.css";
import { useSelectedBranch } from "../../../context/BranchContext";


const uid = () => Math.random().toString(36).substr(2, 9);
const fmt = (n) => new Intl.NumberFormat("es-PA", { style: "currency", currency: "USD" }).format(n);
const cx = (...classes) => classes.filter(Boolean).join(" ");

// ─── ICONS ───────────────────────
const I = {
  Plus: ({ s = 16 }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>,
  Search: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>,
  Upload: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>,
  X: ({ s = 18 }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>,
  Phone: () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="5" y="2" width="14" height="20" rx="2" /><line x1="12" y1="18" x2="12.01" y2="18" /></svg>,
  Monitor: () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></svg>,
  Expand: () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" /><line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" /></svg>,
  Package: () => <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /></svg>,
  Pencil: ({ s = 14 }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" /></svg>,
  Trash: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>,
  Tag: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg>,
  Layers: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></svg>,
  Sparkles: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M12 3l1.5 5.5L19 10l-5.5 1.5L12 17l-1.5-5.5L5 10l5.5-1.5L12 3z" /></svg>,
  FileSpreadsheet: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="8" y1="13" x2="16" y2="13" /><line x1="8" y1="17" x2="16" y2="17" /><line x1="10" y1="9" x2="10" y2="21" /></svg>,
  ChevronRight: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="9 18 15 12 9 6" /></svg>,
  ChevronDown: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="6 9 12 15 18 9" /></svg>,
  Puzzle: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M19.439 7.85c-.049.322.059.648.289.878l1.568 1.568c.47.47.706 1.087.706 1.704s-.235 1.233-.706 1.704l-1.611 1.611a.98.98 0 0 1-.837.276c-.47-.07-.802-.48-.968-.925a2.501 2.501 0 1 0-3.214 3.214c.446.166.855.497.925.968a.979.979 0 0 1-.276.837l-1.61 1.61a2.404 2.404 0 0 1-1.705.707 2.402 2.402 0 0 1-1.704-.706l-1.568-1.568a1.026 1.026 0 0 0-.877-.29c-.493.074-.84.504-1.02.968a2.5 2.5 0 1 1-3.237-3.237c.464-.18.894-.527.967-1.02a1.026 1.026 0 0 0-.289-.877l-1.568-1.568A2.402 2.402 0 0 1 1.998 12c0-.617.236-1.234.706-1.704L4.315 8.685a.98.98 0 0 1 .837-.276c.47.07.802.48.968.925a2.501 2.501 0 1 0 3.214-3.214c-.446-.166-.855-.497-.925-.968a.979.979 0 0 1 .276-.837l1.61-1.61a2.404 2.404 0 0 1 1.705-.707c.618 0 1.234.236 1.704.706l1.568 1.568c.23.23.556.338.877.29.493-.074.84-.504 1.02-.969a2.5 2.5 0 1 1 3.237 3.237c-.464.18-.894.527-.967 1.02z" /></svg>,
  Receipt: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1z" /><line x1="8" y1="6" x2="16" y2="6" /><line x1="8" y1="10" x2="16" y2="10" /><line x1="8" y1="14" x2="12" y2="14" /></svg>,
  Barcode: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M3 5v14" /><path d="M8 5v14" /><path d="M12 5v14" /><path d="M17 5v14" /><path d="M21 5v14" /></svg>,
  Image: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>,
  Check: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>,
  Help: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>,
};

// ─── VIEW MODES ────────────────────────────────────────────────
const VIEW_MODES = [
  { id: "full", label: "Escritorio completo", icon: <I.Expand />, mainWidth: "1fr" },
  { id: "desktop", label: "Vista Desktop (POS)", icon: <I.Monitor />, mainWidth: "820px" },
  { id: "mobile", label: "Vista Mobile (Ecommerce)", icon: <I.Phone />, mainWidth: "420px" },
];

// ─── ONBOARDING TOUR ───────────────────────────────────────────
// Sube la versión (v2, v3...) si cambias los pasos y quieres que vuelva a mostrarse.
const PRODUCT_TOUR_KEY = "pp_onboarding_v2";
// Pasos de introducción y navegación (sin acciones). Los pasos del off-canvas se
// arman dentro del componente porque necesitan abrir/cerrar el panel de detalle.
const PRODUCT_TOUR_BASE_STEPS = [
  { title: "Bienvenido a Productos 👋", content: "Te doy un recorrido rápido para que conozcas cómo administrar tu menú de POS y Ecommerce. Toma menos de un minuto." },
  { target: '[data-tour="branch"]', title: "Sucursales", content: "Cambia entre una sucursal específica o mira todas a la vez. Los precios y la disponibilidad se ajustan por sucursal.", placement: "bottom" },
  { target: '[data-tour="view"]', title: "Vista previa", content: "Previsualiza cómo se verá tu menú en el POS de escritorio o en el Ecommerce móvil.", placement: "bottom" },
  { target: '[data-tour="import"]', title: "Importar desde Excel", content: "¿Tienes muchos productos? Cárgalos de forma masiva usando nuestras plantillas de Excel.", placement: "bottom" },
  { target: '[data-tour="category"]', title: "Categorías", content: "Crea categorías (Hamburguesas, Bebidas, etc.) para mantener tu menú organizado.", placement: "bottom" },
  { target: '[data-tour="product"]', title: "Nuevo producto", content: "Agrega productos y asígnales precio, impuestos, imagen y segmentos.", placement: "bottom" },
  { target: '[data-tour="categories"]', title: "Filtra por categoría", content: "Usa estas pestañas para filtrar rápidamente los productos de cada categoría.", placement: "bottom" },
  { target: '[data-tour="search"]', title: "Búsqueda", content: "Encuentra cualquier producto por su nombre al instante.", placement: "bottom" },
];

// ─── AVAILABLE TAXES ───────────────────────────────────────────
const DEFAULT_CATEGORY_EMOJI = "📂";
const DEFAULT_PRODUCT_EMOJI = "🍽️";

const asArray = (value) => Array.isArray(value) ? value : [];

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "null") || {};
  } catch (err) {
    return {};
  }
};

const getBusinessId = (user) => user?.business_id || user?.business?.id || user?.businessId || 0;
const getBranchId = (branch) => branch?.branchoffice_id || branch?.id || 0;
const getBranchName = (branch) => branch?.branchoffice_name || branch?.name || branch?.code || "Sucursal";
const getApiError = (err, fallback = "No se pudo completar la acción.") => (
  err?.response?.data?.error || err?.response?.data?.message || fallback
);
const getGroupId = (product) => product?.groupId || product?.group_id || product?.id;
const getSegmentId = (segment) => segment?.segmentId || segment?.segment_id || segment?.id;
const getItemId = (item) => item?.itemId || item?.item_id || item?.id;
const getTaxId = (tax) => tax?.tax_id || tax?.id;
const toApiFlag = (value) => (value === true || value === 1 || value === "1" || value === "yes" ? "1" : "0");
const toSegmentPrice = (value) => {
  const amount = Number.parseFloat(value);
  return Number.isFinite(amount) ? amount.toFixed(2) : "0.00";
};

const buildGroupFormData = (product, user, includeBusiness = false) => {
  const formData = new FormData();
  if (includeBusiness) {
    formData.append("business_id", getBusinessId(user));
    if (user?.business?.posIntegration !== undefined) {
      formData.append("pos_integration", user.business.posIntegration);
    }
  }

  formData.append("name", product.name || "Producto sin nombre");
  formData.append("price", product.price || 0);
  formData.append("description", product.description || "");
  formData.append("type", product.type || "normal");
  formData.append("status", product.active === false ? "0" : "1");
  formData.append("preparation", toApiFlag(product.preparation ?? "yes"));
  formData.append("favorite", toApiFlag(product.favorite));
  formData.append("cross_sell", toApiFlag(product.crossSell));
  formData.append("can_be_paid_with_points", toApiFlag(product.pointsPay));
  formData.append("external_reference", product.externalRef || "");
  formData.append("condition_terms", product.terms || "");
  formData.append("icon", product.emoji || product.icon || DEFAULT_PRODUCT_EMOJI);
  if (product.upc) {
    formData.append("upc_code", product.upc);
  }
  if (product.file) {
    formData.append("files", product.file);
  }
  asArray(product.taxes).forEach(tax => formData.append("taxes", JSON.stringify(getTaxId(tax))));
  return formData;
};

const buildBranchGroupPayload = (product) => ({
  group_id: getGroupId(product),
  status: product.active === false ? 0 : 1,
  price: product.price || 0,
  preparation: toApiFlag(product.preparation ?? "yes"),
  favorite: toApiFlag(product.favorite),
  cross_sell: toApiFlag(product.crossSell),
  group_type: product.groupType || product.group_type || "infinite",
  can_be_paid_with_points: toApiFlag(product.pointsPay),
  points_value: product.pointsValue || product.points_value || 0,
});

const normalizeCategory = (category, index = 0) => ({
  id: String(category?.category_id || category?.id || `category-${index}`),
  name: category?.category_name || category?.name || "Sin nombre",
  emoji: category?.emoji || DEFAULT_CATEGORY_EMOJI,
  description: category?.secundaryName || category?.description || "",
  active: category?.status !== 0,
  availableEcommerce: category?.available_ecommerce === 1,
  raw: category,
});

const getProductCategoryIds = (product) => {
  const ids = [
    product?.category_id,
    product?.categoryId,
    product?.category?.id,
    product?.category?.category_id,
  ];

  asArray(product?.categoryIds).forEach(categoryId => ids.push(categoryId));
  asArray(product?.categories).forEach(category => ids.push(category?.category_id || category?.id));
  asArray(product?.category_per_groups).forEach(category => ids.push(category?.category_id || category?.id));

  return [...new Set(ids.filter(Boolean).map(id => String(id)))];
};

const normalizeTax = (tax, index = 0) => ({
  id: String(tax?.tax_id || tax?.id || `tax-${index}`),
  name: tax?.tax || tax?.name || "Impuesto",
  rate: tax?.percentage || tax?.rate || "",
});

const isDefaultSegmentItem = (item, defaultItems) => {
  const itemId = item?.item_id || item?.id;
  return asArray(defaultItems).some(defaultItem => (
    String(defaultItem?.item_id || defaultItem?.id) === String(itemId)
  ));
};

const normalizeSegmentOption = (item, index = 0, defaultItems = []) => ({
  id: String(item?.item_id || item?.id || `option-${index}`),
  itemId: item?.item_id || item?.id,
  name: item?.name || "Producto sin nombre",
  description: item?.description || "",
  price: Number(item?.price) || 0,
  imageUrl: buildCloudinaryImageUrl({ url: item?.img || item?.imageUrl || item?.image_url, width: 200, quality: "auto", type: "image" }),
  img: item?.img || "",
  preparation: item?.item_preparation ?? item?.preparation,
  isDefault: Boolean(item?.isDefault || isDefaultSegmentItem(item, defaultItems)),
  taxes: asArray(item?.taxes).map(normalizeTax),
  raw: item,
});

const normalizeSegment = (segment, index = 0) => {
  const defaultItems = asArray(segment?.default_items);
  const sourceOptions = asArray(segment?.items).length > 0 ? asArray(segment?.items) : asArray(segment?.options);

  return {
    id: String(segment?.segment_id || segment?.id || `segment-${index}`),
    segmentId: segment?.segment_id || segment?.id,
    name: segment?.name || "Segmento sin nombre",
    description: segment?.description || "",
    minimumCount: segment?.minimumCount || segment?.minimum_count || 0,
    maximumCount: segment?.maximumCount || segment?.maximum_count || 0,
    type: segment?.type || segment?.segment_type || segment?.segment_type_id || "",
    options: sourceOptions.map((item, itemIndex) => normalizeSegmentOption(item, itemIndex, defaultItems)),
    defaultItemIds: defaultItems.map(item => String(item?.item_id || item?.id)).filter(Boolean),
    raw: segment,
  };
};

const normalizeProduct = (product, index = 0) => {
  const categoryIds = getProductCategoryIds(product);
  const categoryId = categoryIds[0] || "";
  const upcCodes = asArray(product?.upc_codes);
  const imageUrl = buildCloudinaryImageUrl({ url: product?.img || product?.imageUrl || product?.image_url, width: 400, quality: "auto", type: "image" });

  return {
    id: product?.group_id || product?.id || `product-${index}`,
    groupId: product?.group_id || product?.id,
    name: product?.name || "Producto sin nombre",
    categoryId,
    categoryIds,
    price: Number(product?.branchoffice_price ?? product?.price) || 0,
    emoji: product?.icon || product?.emoji || DEFAULT_PRODUCT_EMOJI,
    imageUrl,
    img: product?.img || "",
    description: product?.description || "",
    active: product?.status !== 0,
    segments: asArray(product?.segments || product?.segment_per_groups).map(normalizeSegment),
    taxes: asArray(product?.taxes).map(normalizeTax),
    type: product?.type || "normal",
    preparation: (product?.branchoffice_preparation ?? product?.preparation) === 0 ? "no" : "yes",
    favorite: product?.favorite === 1 || product?.favorite === true,
    crossSell: product?.cross_sell === 1 || product?.cross_sell === true,
    groupType: product?.group_type || "infinite",
    externalRef: product?.external_reference || "",
    pointsPay: Boolean(product?.can_be_paid_with_points),
    pointsValue: product?.points_value || 0,
    terms: product?.condition_terms || "",
    upc: upcCodes[0]?.upc_code || product?.upc || "",
    raw: product,
  };
};

// ─── MAIN APP ──────────────────────────────────────────────────
export default function ProductPanel() {
  const user = getStoredUser();
  const token = localStorage.getItem("x-access-token");
  const businessId = getBusinessId(user);
  const branchOptions = asArray(user?.branchoffices);

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const { selectedBranch, setSelectedBranch } = useSelectedBranch();
  //This panel's own concept of "selected branch" has always been the internal branchoffice_id
  //(used directly in its API calls), while other pages key off the branch `code` — the shared
  //context resolves either half against the user's branch list, so both stay in sync here.
  const selectedBranchId = selectedBranch.branchoffice_id || selectedBranch.id || 0;
  const setSelectedBranchId = (id) => {
    const numId = Number(id);
    setSelectedBranch(numId ? { branchoffice_id: numId, id: numId } : { code: 0 });
  };
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const [viewMode, setViewMode] = useState("full");
  const [viewDropdown, setViewDropdown] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showBranchAssignModal, setShowBranchAssignModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingProductInPanel, setEditingProductInPanel] = useState(false);
  const [offCanvasTab, setOffCanvasTab] = useState("segments");
  const [loadingMenu, setLoadingMenu] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [tourSignal, setTourSignal] = useState(0);
  const tourActionsRef = useRef({});

  const isFirstTime = categories.length === 0 && products.length === 0;
  const currentView = VIEW_MODES.find(v => v.id === viewMode);
  const isBranchView = Number(selectedBranchId) !== 0;

  const filteredProducts = products.filter(p => {
    const matchCat = activeCategory === "all" || p.categoryId === activeCategory || p.categoryIds?.includes(activeCategory);
    const matchSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCat && matchSearch;
  });

  const loadPanelData = useCallback(async () => {
    if (!token || !businessId) {
      setLoadError("No hay sesión activa para cargar el menú.");
      return;
    }

    try {
      setLoadingMenu(true);
      setLoadError("");

      const [categoryResponse, groupsResponse] = await Promise.all([
        isBranchView
          ? CategoriesDashFetchAPI.getCategoriesByBranchOffice(selectedBranchId, token)
          : CategoriesDashFetchAPI.getCategoriesByBusiness(businessId, token),
        isBranchView
          ? GroupsFechtAPI.getGroupsByBranchoffice(selectedBranchId, token)
          : GroupsFechtAPI.getGroupsByBusiness(businessId, token),
      ]);

      const nextCategories = asArray(categoryResponse?.data?.data).map(normalizeCategory);
      const nextProducts = asArray(groupsResponse?.data?.data)
        .filter(product => product?.type === "normal" || product?.type === "reservable")
        .map(normalizeProduct);

      setCategories(nextCategories);
      setProducts(nextProducts);
      setShowWelcome(nextCategories.length === 0 && nextProducts.length === 0);
    } catch (err) {
      console.log(err);
      setLoadError(err?.response?.data?.error || err?.response?.data?.message || "No se pudo cargar el menú.");
      setCategories([]);
      setProducts([]);
    } finally {
      setLoadingMenu(false);
    }
  }, [businessId, isBranchView, selectedBranchId, token]);

  useEffect(() => {
    loadPanelData();
  }, [loadPanelData]);

  const addCategory = async (cat) => {
    try {
      const formData = new FormData();
      formData.append("name", cat.name);
      formData.append("description", cat.description || cat.name);
      formData.append("business_id", businessId);
      formData.append("branchoffice_id", isBranchView ? selectedBranchId : "null");
      formData.append("category_type", cat.categoryTypeId);

      await CategoriesDashFetchAPI.createCategorie(formData, token);
      setShowCategoryModal(false);
      setShowWelcome(false);
      ErrorAlert("", "Categoría creada con éxito", "success");
      await loadPanelData();
      return true;
    } catch (err) {
      console.log(err);
      ErrorAlert(getApiError(err, "No se pudo crear la categoría."), "Error de registro", "error");
      return false;
    }
  };
  const updateCategory = async (u) => {
    try {
      const formData = new FormData();
      formData.append("name", u.name);
      formData.append("description", u.description || u.name);

      await CategoriesDashFetchAPI.UpdateCategories(formData, u.id, token);

      if (isBranchView) {
        await CategoriesDashFetchAPI.UpdateCategoriesByBranchOffice(
          { available_ecommerce: u.availableEcommerce ? 1 : 0, status: u.active === false ? 0 : 1 },
          u.id,
          selectedBranchId,
          token
        );
      }

      ErrorAlert("", "Categoría actualizada con éxito", "success");
      setEditingCategory(null);
      await loadPanelData();
    } catch (err) {
      console.log(err);
      ErrorAlert(getApiError(err, "No se pudo actualizar la categoría."), "Error de actualización", "error");
    }
  };
  const syncProductState = (u) => {
    setProducts(p => p.map(x => x.id === u.id ? u : x));
    setSelectedProduct(u);
  };
  const applyProductCategories = (product, categoryIds) => {
    const nextCategoryIds = categoryIds.filter(Boolean).map(id => String(id));
    return {
      ...product,
      categoryId: nextCategoryIds[0] || "",
      categoryIds: nextCategoryIds,
    };
  };
  const updateProductCategories = (groupId, updater) => {
    setProducts(prev => prev.map(product => {
      if (String(getGroupId(product)) !== String(groupId)) return product;
      const nextCategoryIds = updater(getProductCategoryIds(product));
      return applyProductCategories(product, nextCategoryIds);
    }));
    setSelectedProduct(prev => {
      if (!prev || String(getGroupId(prev)) !== String(groupId)) return prev;
      const nextCategoryIds = updater(getProductCategoryIds(prev));
      return applyProductCategories(prev, nextCategoryIds);
    });
  };
  const addProduct = async (prod) => {
    try {
      const formData = buildGroupFormData(prod, user, true);
      const response = await GroupsFechtAPI.newGroup(formData, token);
      const responseData = response?.data?.data || response?.data || {};
      const createdGroupId = responseData?.group_id || responseData?.id || responseData?.createdProductId;
      const created = normalizeProduct({ ...prod, ...responseData, group_id: createdGroupId });
      const groupId = getGroupId(created);

      if (prod.categoryId && groupId) {
        await GroupsFechtAPI.asingproductToCategory(prod.categoryId, groupId, token);
      }

      if (isBranchView && groupId) {
        // El backend ya crea el grupo asignado a todas las sucursales del negocio
        // (mismo comportamiento que el flujo viejo de /products, que nunca llama a
        // "asingGroupToBranchoffice" al crear). Por eso este intento de asignación
        // puede fallar con "ya esta asignado": lo ignoramos y seguimos con el update,
        // que es el que realmente guarda el precio/estado propios de esta sucursal.
        try {
          await GroupsFechtAPI.asingGroupToBranchoffice(selectedBranchId, { group_id: groupId }, token);
        } catch (assignErr) {
          console.log(assignErr);
        }
        await GroupsFechtAPI.updateGroupBranchoffice(selectedBranchId, buildBranchGroupPayload({ ...prod, groupId }), token);
      }

      setShowProductModal(false);
      ErrorAlert("", "Producto creado con éxito", "success");
      await loadPanelData();
      return true;
    } catch (err) {
      console.log(err);
      ErrorAlert(getApiError(err, "No se pudo crear el producto."), "Error de registro", "error");
      return false;
    }
  };
  const updateProduct = async (u) => {
    const groupId = getGroupId(u);
    const currentProduct = products.find(product => String(getGroupId(product)) === String(groupId));
    if (!groupId) {
      syncProductState(u);
      setEditingProductInPanel(false);
      return true;
    }

    try {
      await GroupsFechtAPI.updateGroup(groupId, buildGroupFormData(u, user), token);
      if (isBranchView) {
        await GroupsFechtAPI.updateGroupBranchoffice(selectedBranchId, buildBranchGroupPayload({ ...u, groupId }), token);
      }
      const nextProduct = {
        ...u,
        groupId,
        categoryId: currentProduct?.categoryId || u.categoryId,
        categoryIds: currentProduct?.categoryIds || u.categoryIds || [],
      };
      syncProductState(nextProduct);
      setEditingProductInPanel(false);
      ErrorAlert("", "Producto actualizado con éxito", "success");
      return true;
    } catch (err) {
      console.log(err);
      ErrorAlert(getApiError(err, "No se pudo actualizar el producto."), "Error de actualización", "error");
      return false;
    }
  };
  const addProductCategory = async (product, categoryId) => {
    const groupId = getGroupId(product);
    if (!groupId || !categoryId) return;

    const currentCategoryIds = getProductCategoryIds(product);
    if (currentCategoryIds.includes(String(categoryId))) return;

    updateProductCategories(groupId, ids => ids.includes(String(categoryId)) ? ids : [...ids, String(categoryId)]);

    try {
      await GroupsFechtAPI.asingproductToCategory(categoryId, groupId, token);
    } catch (err) {
      updateProductCategories(groupId, ids => ids.filter(id => id !== String(categoryId)));
      console.log(err);
      ErrorAlert(getApiError(err, "No se pudo asignar la categoría."), "Error de asignación", "error");
    }
  };
  const removeProductCategory = async (product, categoryId) => {
    const groupId = getGroupId(product);
    const currentCategoryIds = getProductCategoryIds(product);
    if (!groupId || !categoryId) return;

    if (currentCategoryIds.length <= 1) {
      ErrorAlert("El producto debe estar asignado al menos a una categoría.", "Categorías", "warning");
      return;
    }

    updateProductCategories(groupId, ids => ids.filter(id => id !== String(categoryId)));

    try {
      await GroupsFechtAPI.deleteCategoryProduct(categoryId, { group_id: groupId }, token);
    } catch (err) {
      updateProductCategories(groupId, ids => ids.includes(String(categoryId)) ? ids : [...ids, String(categoryId)]);
      console.log(err);
      ErrorAlert(getApiError(err, "No se pudo remover la categoría."), "Error de categorías", "error");
    }
  };
  const toggleProductBranchStatus = async (product, active) => {
    const groupId = getGroupId(product);
    if (!isBranchView || !groupId) return;

    const nextProduct = { ...product, active };
    try {
      await GroupsFechtAPI.updateGroupBranchoffice(selectedBranchId, buildBranchGroupPayload({ ...nextProduct, groupId }), token);
      setProducts(prev => prev.map(item => getGroupId(item) === groupId ? { ...item, active } : item));
      setSelectedProduct(prev => prev && getGroupId(prev) === groupId ? { ...prev, active } : prev);
    } catch (err) {
      console.log(err);
      ErrorAlert(getApiError(err, "No se pudo actualizar el estado del producto."), "Error de actualización", "error");
    }
  };
  const deleteProduct = async (id) => {
    const product = products.find(item => item.id === id);
    const groupId = getGroupId(product);
    if (!groupId) {
      setProducts(p => p.filter(x => x.id !== id));
      setSelectedProduct(null);
      return;
    }

    try {
      if (isBranchView) {
        await GroupsFechtAPI.deleteProductbybranchoffice(selectedBranchId, { group_id: groupId }, token);
      } else {
        await GroupsFechtAPI.deleteProductbybusiness(businessId, { group_id: groupId }, token);
      }
      setProducts(p => p.filter(x => x.id !== id));
      setSelectedProduct(null);
      ErrorAlert("", "Producto eliminado con éxito", "success");
    } catch (err) {
      console.log(err);
      ErrorAlert(getApiError(err, "No se pudo eliminar el producto."), "Error al eliminar", "error");
    }
  };
  const getCatName = (id) => categories.find(c => c.id === id)?.name || "";
  const getCatEmoji = (id) => categories.find(c => c.id === id)?.emoji || "📂";
  const getCatCount = (id) => id === "all" ? products.length : products.filter(p => p.categoryId === id || p.categoryIds?.includes(id)).length;
  const changeBranch = (branchId) => {
    setSelectedBranchId(Number(branchId));
    setSelectedProduct(null);
    setActiveCategory("all");
    setSearchQuery("");
  };
  const selectProduct = async (p) => {
    setSelectedProduct({ ...p, segmentsLoading: Boolean(p.groupId && token) });
    setEditingProductInPanel(false);
    setOffCanvasTab("segments");

    if (!p.groupId || !token) {
      return;
    }

    try {
      const [groupResult, segmentsResult] = await Promise.allSettled([
        GroupsFechtAPI.getGroupById(p.groupId, token),
        SegmentsFechtAPI.getSegmentsByGroup(p.groupId, token),
      ]);

      const groupData = groupResult.status === "fulfilled" ? groupResult.value?.data?.data || {} : {};
      const nextSegments = segmentsResult.status === "fulfilled"
        ? asArray(segmentsResult.value?.data?.data).map(normalizeSegment)
        : p.segments || [];
      const normalizedDetail = normalizeProduct({ ...p.raw, ...groupData, segments: nextSegments });
      const detail = {
        ...normalizedDetail,
        ...(isBranchView ? {
          price: p.price,
          active: p.active,
          preparation: p.preparation,
          favorite: p.favorite,
          crossSell: p.crossSell,
          groupType: p.groupType,
          pointsPay: p.pointsPay,
          pointsValue: p.pointsValue,
        } : {}),
        segmentsLoading: false,
      };
      setSelectedProduct(detail);
      setProducts(prev => prev.map(product => product.id === p.id ? { ...product, ...detail } : product));
    } catch (err) {
      console.log(err);
    }
  };

  const mainMaxWidth = currentView.mainWidth;

  // ── Acciones que el tour dispara al entrar a ciertos pasos ──
  // Se reasignan en cada render para cerrar sobre el estado más reciente.
  tourActionsRef.current.closeOffCanvasStep = () => {
    setSelectedProduct(null);
    // Devuelve false (y se salta el paso) si no hay productos que mostrar.
    return (filteredProducts.length + products.length) > 0;
  };
  tourActionsRef.current.openFirstProduct = () => {
    const target = filteredProducts[0] || products[0];
    if (!target) return false; // sin productos → se salta la sección del off-canvas
    selectProduct(target);
    return true;
  };

  const tourSteps = useMemo(() => ([
    ...PRODUCT_TOUR_BASE_STEPS,
    {
      target: '[data-tour="products"]',
      title: "Tus productos",
      content: "Toca cualquier producto para ver y editar su detalle. Te muestro cómo se ve 👇",
      placement: "top",
      action: () => tourActionsRef.current.closeOffCanvasStep?.(),
    },
    {
      target: '[data-tour="oc-edit"]',
      title: "Editar el producto",
      content: "Toca el lápiz para editar el nombre, precio, imagen, descripción, impuestos y más de este producto.",
      placement: "left",
      action: () => tourActionsRef.current.openFirstProduct?.(),
    },
    {
      target: '[data-tour="oc-tabs"]',
      title: "Segmentos e impuestos",
      content: "Aquí alternas entre los Segmentos (modificadores como tamaños o extras) y los Impuestos del producto.",
      placement: "left",
    },
    {
      target: '[data-tour="oc-segments"]',
      title: "Agregar modificadores",
      content: "Agrega segmentos y, dentro de cada uno, asígnales productos individuales para armar tus combos y opciones.",
      placement: "left",
    },
    {
      target: '[data-tour="oc-delete"]',
      title: "Eliminar producto",
      content: "Si necesitas quitar el producto, elimínalo desde aquí. Te pediremos confirmación antes de borrarlo.",
      placement: "left",
    },
    {
      target: '[data-tour="oc-close"]',
      title: "¡Listo! 🎉",
      content: "Cuando termines, cierra el panel desde aquí. Ya sabes lo esencial para administrar tu menú.",
      placement: "left",
    },
  ]), []);

  return (
    <AppShell route="products">
      <MainContent>
        <div className="pp-root">
          {/* ── LAYOUT ── */}
          <div className="pp-layout">
            {/* Main column */}
            <div
              className={cx("pp-main", viewMode !== "full" && "pp-main--framed")}
              style={{
                width: viewMode === "full" ? "auto" : "100%",
                flex: viewMode === "full" ? "1 1 0" : "0 1 auto",
                maxWidth: viewMode === "full" ? "none" : mainMaxWidth,
              }}
            >
              {/* Top bar */}
              <div className="pp-topbar">
                <div className="pp-topbar-title"><I.Layers /> Productos</div>
                <div className="pp-topbar-actions">
                  <select data-tour="branch" className="pp-form-select pp-branch-select" value={selectedBranchId} onChange={e => changeBranch(e.target.value)} title="Sucursal">
                    <option value={0}>Todas las sucursales</option>
                    {branchOptions.map((branch, index) => {
                      const branchId = getBranchId(branch);
                      return <option key={branchId || index} value={branchId}>{getBranchName(branch)}</option>;
                    })}
                  </select>
                  {/* View mode dropdown */}
                  <div data-tour="view" style={{ position: "relative" }}>
                    <button className="pp-btn-secondary" onClick={() => setViewDropdown(!viewDropdown)}>
                      {currentView.icon}
                      <span className="pp-view-btn-label">{currentView.label}</span>
                      <I.ChevronDown />
                    </button>
                    {viewDropdown && (
                      <>
                        <div className="pp-view-scrim" onClick={() => setViewDropdown(false)} />
                        <div className="pp-dropdown">
                          {VIEW_MODES.map(v => (
                            <button key={v.id} className={cx("pp-dropdown-item", viewMode === v.id && "pp-dropdown-item--active")}
                              onClick={() => { setViewMode(v.id); setViewDropdown(false); }}>
                              {v.icon}
                              <span>{v.label}</span>
                              {viewMode === v.id && <span className="pp-dropdown-check"><I.Check /></span>}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  <Btn t="s" data-tour="import" onClick={() => setShowImportModal(true)}><I.Upload /> Importar</Btn>
                  <Btn t="s" data-tour="category" onClick={() => setShowCategoryModal(true)}><I.Tag /> Categoría</Btn>
                  {isBranchView && <Btn t="s" onClick={() => setShowBranchAssignModal(true)}><I.Plus /> Asignar</Btn>}
                  {categories.length > 0 && <Btn t="p" data-tour="product" onClick={() => setShowProductModal(true)}><I.Plus /> Producto</Btn>}
                  <button className="pp-help-btn" title="Ver recorrido guiado" onClick={() => setTourSignal(s => s + 1)}><I.Help /></button>
                </div>
              </div>

              {/* Categories */}
              {categories.length > 0 && (
                <div className="pp-cat-strip" data-tour="categories">
                  <CatChip label="Todas" count={products.length} active={activeCategory === "all"} onClick={() => setActiveCategory("all")} />
                  {categories.map(c => (
                    <CatChip key={c.id} label={`${c.emoji} ${c.name}`} count={getCatCount(c.id)} active={activeCategory === c.id} onClick={() => setActiveCategory(c.id)} onEdit={() => setEditingCategory(c)} />
                  ))}
                </div>
              )}

              {/* Search */}
              {products.length > 0 && (
                <div className="pp-search" data-tour="search">
                  <div className="pp-search-field">
                    <div className="pp-search-icon"><I.Search /></div>
                    <input className="pp-search-input" placeholder="Buscar productos..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
                  </div>
                </div>
              )}

              {/* Products */}
              <div className="pp-product-area">
                {loadingMenu ? (
                  <EmptyState title="Cargando menú" desc="Estamos cargando categorías y productos." />
                ) : loadError ? (
                  <EmptyState title="No se pudo cargar el menú" desc={loadError}
                    actions={<Btn t="p" onClick={loadPanelData}>Reintentar</Btn>} />
                ) : isFirstTime && !showWelcome ? (
                  <EmptyState title="Comienza creando tu menú" desc="Crea categorías y agrega productos para configurar tu tienda."
                    actions={<><Btn t="s" onClick={() => setShowImportModal(true)}><I.Upload /> Importar Excel</Btn><Btn t="p" onClick={() => setShowCategoryModal(true)}><I.Plus /> Crear categoría</Btn></>} />
                ) : categories.length > 0 && filteredProducts.length === 0 ? (
                  <EmptyState title={searchQuery ? "Sin resultados" : "No hay productos"} desc={searchQuery ? `No se encontraron productos para "${searchQuery}"` : "Agrega tu primer producto."}
                    actions={!searchQuery && <Btn t="p" onClick={() => setShowProductModal(true)}><I.Plus /> Crear producto</Btn>} />
                ) : (
                  <div className="pp-product-grid" data-tour="products" style={{ gridTemplateColumns: viewMode === "mobile" ? "repeat(auto-fill, minmax(160px, 1fr))" : "repeat(auto-fill, minmax(200px, 1fr))" }}>
                    {filteredProducts.map(p => (
                      <div key={p.id} className={cx("pp-product-card", hoveredProduct === p.id && "pp-product-card--hover")}
                        onClick={() => selectProduct(p)} onMouseEnter={() => setHoveredProduct(p.id)} onMouseLeave={() => setHoveredProduct(null)}>
                        <div className="pp-product-img">
                          {p.imageUrl ? <img src={p.imageUrl} alt="" /> : <span className="pp-product-emoji">{p.emoji}</span>}
                          {isBranchView && <span className={cx("pp-badge", !p.active && "pp-badge--off")}>{p.active ? "Activo" : "Inactivo"}</span>}
                        </div>
                        <div className="pp-product-info">
                          <div className="pp-product-name" title={p.name}>{p.name}</div>
                          <div className="pp-product-cat">{getCatName(p.categoryId)}</div>
                          {isBranchView && <div className="pp-product-price">{fmt(p.price)}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* ── OFF-CANVAS ── */}
            {selectedProduct && (
              <>
                <div className="pp-offcanvas-backdrop" onClick={() => setSelectedProduct(null)} />
                <div className="pp-offcanvas">
                  <div className="pp-offcanvas-header">
                    <span className="pp-offcanvas-title">Producto</span>
                    <button className="pp-btn-icon" data-tour="oc-close" onClick={() => setSelectedProduct(null)}><I.X /></button>
                  </div>
                  <div className="pp-offcanvas-body">
                    <ProductDetailCard
                      product={selectedProduct} catName={getCatName(selectedProduct.categoryId)} catEmoji={getCatEmoji(selectedProduct.categoryId)}
                      editing={editingProductInPanel} onToggleEdit={() => setEditingProductInPanel(!editingProductInPanel)}
                      categories={categories} isBranchView={isBranchView} onSave={updateProduct} onCancel={() => setEditingProductInPanel(false)}
                      onToggleActive={(active) => toggleProductBranchStatus(selectedProduct, active)}
                      onAddCategory={(categoryId) => addProductCategory(selectedProduct, categoryId)}
                      onRemoveCategory={(categoryId) => removeProductCategory(selectedProduct, categoryId)} />
                    {!editingProductInPanel && (
                      <>
                        <div className="pp-tab-bar" data-tour="oc-tabs">
                          {[{ id: "segments", label: "Segmentos", icon: <I.Puzzle /> }, { id: "taxes", label: "Impuestos", icon: <I.Receipt /> }].map(t => (
                            <button key={t.id} className={cx("pp-tab", offCanvasTab === t.id && "pp-tab--active")} onClick={() => setOffCanvasTab(t.id)}>{t.icon} {t.label}</button>
                          ))}
                        </div>
                        <div className="pp-offcanvas-tabpanel">
                          {offCanvasTab === "segments" && <ConnectedSegmentsTab product={selectedProduct} onUpdate={syncProductState} />}
                          {offCanvasTab === "taxes" && <TaxesTab product={selectedProduct} onUpdate={syncProductState} />}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="pp-offcanvas-footer">
                    <Btn t="s" onClick={() => setSelectedProduct(null)} style={{ flex: 1, justifyContent: "center" }}>Cerrar</Btn>
                    <button className="pp-btn-ghost pp-danger" data-tour="oc-delete" onClick={() => setConfirmDelete(true)}><I.Trash /> Eliminar</button>
                  </div>
                </div>

                {confirmDelete && (
                  <ConfirmDeleteModal
                    productName={selectedProduct.name}
                    onCancel={() => setConfirmDelete(false)}
                    onConfirm={() => { deleteProduct(selectedProduct.id); setConfirmDelete(false); }}
                  />
                )}
              </>
            )}
          </div>

          {/* MODALS */}
          {showWelcome && isFirstTime && !loadingMenu && !loadError && <WelcomeModal onCreateCategory={() => { setShowWelcome(false); setShowCategoryModal(true); }} onImport={() => { setShowWelcome(false); setShowImportModal(true); }} onClose={() => setShowWelcome(false)} />}
          {showCategoryModal && <CategoryModal onSave={addCategory} onClose={() => setShowCategoryModal(false)} />}
          {editingCategory && <CategoryEditModal category={editingCategory} isBranchView={isBranchView} onSave={updateCategory} onClose={() => setEditingCategory(null)} />}
          {showProductModal && categories.length > 0 && <ProductModal categories={categories} isBranchView={isBranchView} onSave={addProduct} onClose={() => setShowProductModal(false)} />}
          {showBranchAssignModal && <BranchAssignModal businessId={businessId} branchId={selectedBranchId} currentProducts={products} onClose={() => setShowBranchAssignModal(false)} onAssigned={loadPanelData} />}
          {showImportModal && <ImportModal onClose={() => setShowImportModal(false)} onImported={loadPanelData} />}

          {/* ONBOARDING TOUR */}
          <ProductTour
            steps={tourSteps}
            storageKey={PRODUCT_TOUR_KEY}
            autoStart={!loadingMenu && !loadError && !showWelcome}
            startSignal={tourSignal}
          />
        </div>
      </MainContent>
    </AppShell>
  );
}

// ─── SHARED ────────────────────────────────────────────────────
function Btn({ t = "p", onClick, children, disabled, className, style: sx, ...rest }) {
  const base = t === "p" ? "pp-btn-primary" : t === "s" ? "pp-btn-secondary" : "pp-btn-ghost";
  return <button {...rest} className={cx(base, className)} style={{ opacity: disabled ? 0.5 : 1, ...sx }} onClick={onClick} disabled={disabled}>{children}</button>;
}
function Toggle({ active, onChange }) {
  return <button className={cx("pp-toggle", active && "pp-toggle--on")} onClick={() => onChange(!active)}><div className={cx("pp-toggle-dot", active && "pp-toggle-dot--on")} /></button>;
}
function FG({ label, children }) { return <div className="pp-form-group"><label className="pp-form-label">{label}</label>{children}</div>; }

function CatChip({ label, count, active, onClick, onEdit }) {
  return (
    <div className="pp-cat-chip-wrap">
      <button className={cx("pp-cat-chip", active && "pp-cat-chip--active")} onClick={onClick} title={label}>
        <span className="pp-cat-chip-label">{label}</span><span className={cx("pp-cat-chip-count", active && "pp-cat-chip-count--active")}>{count}</span>
      </button>
      {onEdit && <button className="pp-cat-edit" onClick={e => { e.stopPropagation(); onEdit(); }}><I.Pencil s={10} /></button>}
    </div>
  );
}
function EmptyState({ title, desc, actions }) {
  return <div className="pp-empty"><div className="pp-empty-icon"><I.Package /></div><div className="pp-empty-title">{title}</div><div className="pp-empty-desc">{desc}</div>{actions && <div className="pp-empty-actions">{actions}</div>}</div>;
}

function CategoryAssignmentField({ assignedCategories, availableCategories, categoryToAdd, onChangeCategoryToAdd, onAdd, onRemove }) {
  return (
    <div className="pp-cat-manager">
      <div className="pp-cat-chip-list">
        {assignedCategories.length === 0 ? (
          <span className="pp-cat-chip-empty">Sin categorías asignadas</span>
        ) : assignedCategories.map(category => (
          <span key={category.id} className="pp-cat-chip-assigned">
            <span className="pp-cat-chip-text">{category.emoji} {category.name}</span>
            <button type="button" className="pp-cat-remove" onClick={() => onRemove?.(category.id)} title="Remover categoría">
              <I.X s={11} />
            </button>
          </span>
        ))}
      </div>
      <div className="pp-cat-add-row">
        <select
          className="pp-form-select pp-form-select--sm"
          value={categoryToAdd}
          onChange={e => onChangeCategoryToAdd(e.target.value)}
          disabled={availableCategories.length === 0}
        >
          <option value="">{availableCategories.length === 0 ? "Todas asignadas" : "Agregar categoría"}</option>
          {availableCategories.map(category => <option key={category.id} value={category.id}>{category.emoji} {category.name}</option>)}
        </select>
        <Btn t="s" onClick={onAdd} disabled={!categoryToAdd} style={{ padding: "8px 10px", fontSize: 12 }}>
          <I.Plus s={12} /> Agregar
        </Btn>
      </div>
    </div>
  );
}

// ─── PRODUCT DETAIL CARD (with all config fields in edit mode) ──
function ProductDetailCard({ product, catName, catEmoji, editing, onToggleEdit, categories, isBranchView, onSave, onCancel, onToggleActive, onAddCategory, onRemoveCategory }) {
  const [form, setForm] = useState({ ...product });
  const [categoryToAdd, setCategoryToAdd] = useState("");
  const imageInputRef = useRef(null);
  useEffect(() => {
    setForm(current => {
      if (editing && String(getGroupId(current)) === String(getGroupId(product))) {
        return {
          ...current,
          categoryId: product.categoryId,
          categoryIds: product.categoryIds,
        };
      }

      return { ...product };
    });
  }, [product, editing]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const pickImage = (file) => {
    if (!file) return;
    setForm(f => ({ ...f, file, imageUrl: URL.createObjectURL(file) }));
  };
  const categorySourceIds = asArray(form.categoryIds).length > 0 ? asArray(form.categoryIds) : [form.categoryId];
  const assignedCategoryIds = categorySourceIds.filter(Boolean).map(String);
  const assignedCategories = categories.filter(category => assignedCategoryIds.includes(String(category.id)));
  const availableCategories = categories.filter(category => !assignedCategoryIds.includes(String(category.id)));
  useEffect(() => {
    if (categoryToAdd && !availableCategories.some(category => String(category.id) === String(categoryToAdd))) {
      setCategoryToAdd("");
    }
  }, [availableCategories, categoryToAdd]);

  if (editing) {
    return (
      <div className="pp-detail-card">
        <div className="pp-detail-card-head">
          <span className="pp-detail-title--plain">Editar producto</span>
          <button className="pp-btn-icon" onClick={onCancel}><I.X s={16} /></button>
        </div>
        <div className="pp-edit-body">
          {/* Image upload area */}
          <div className="pp-upload-zone" style={{ marginBottom: 14, height: 110 }} onClick={() => imageInputRef.current?.click()}>
            {form.imageUrl ? (
              <img src={form.imageUrl} alt="" className="pp-upload-preview" />
            ) : (
              <><span className="pp-upload-emoji-lg">{form.emoji}</span><div className="pp-upload-hint"><I.Image /> Subir imagen o elegir ícono</div></>
            )}
          </div>
          <input ref={imageInputRef} hidden type="file" accept="image/*" onChange={e => pickImage(e.target.files?.[0])} />
          {/* Emoji picker */}
          <div className="pp-edit-emoji">
            <label className="pp-form-label pp-edit-label">Ícono</label>
            <EmojiPicker emojis={EMOJIS_PROD} selected={form.emoji} onSelect={e => set("emoji", e)} />
          </div>
          <FG label="Nombre"><input className="pp-form-input" value={form.name} onChange={e => set("name", e.target.value)} /></FG>
          <div style={{ display: "grid", gridTemplateColumns: isBranchView ? "1.3fr 0.7fr" : "1fr", gap: 10 }}>
            <FG label="Categorías">
              <CategoryAssignmentField
                assignedCategories={assignedCategories}
                availableCategories={availableCategories}
                categoryToAdd={categoryToAdd}
                onChangeCategoryToAdd={setCategoryToAdd}
                onAdd={() => {
                  if (!categoryToAdd) return;
                  onAddCategory?.(categoryToAdd);
                  setCategoryToAdd("");
                }}
                onRemove={onRemoveCategory}
              />
            </FG>
            {isBranchView && <FG label="Precio"><input className="pp-form-input" type="number" step="0.01" value={form.price} onChange={e => set("price", parseFloat(e.target.value) || 0)} /></FG>}
          </div>
          <FG label="Descripción"><textarea className="pp-form-textarea" value={form.description} onChange={e => set("description", e.target.value)} placeholder="Describe tu producto..." /></FG>

          {/* ── Config fields (moved here from config tab) ── */}
          <div className="pp-edit-config">
            <div className="pp-edit-config-title">Configuración</div>
            <div className="pp-edit-grid-2">
              <FG label="Tipo">
                <select className="pp-form-select" value={form.type || "normal"} onChange={e => set("type", e.target.value)}>
                  <option value="normal">Normal</option>
                  <option value="combo">Combo</option>
                  <option value="digital">Digital</option>
                </select>
              </FG>
              <FG label="Preparación">
                <select className="pp-form-select" value={form.preparation || "yes"} onChange={e => set("preparation", e.target.value)}>
                  <option value="yes">Sí</option>
                  <option value="no">No</option>
                </select>
              </FG>
            </div>
            <FG label="Referencia externa"><input className="pp-form-input" value={form.externalRef || ""} onChange={e => set("externalRef", e.target.value)} placeholder="No tiene" /></FG>
            <div className="pp-edit-grid-2">
              <div className="pp-toggle-wrap">
                <div><div className="pp-edit-toggle-label">Pago con puntos </div></div>
                <Toggle active={form.pointsPay || false} onChange={v => set("pointsPay", v)} />
              </div>
              {/* {isBranchView && ( */}
              <FG label="Valor en puntos"><input className="pp-form-input" value={form.name} type="number" /></FG>
              {/* )} */}
            </div>


            {/* {isBranchView && ( */}
            <div className="pp-edit-grid-2">
              <div className="pp-toggle-wrap">
                <div><div className="pp-edit-toggle-label">Favorito/Destacado</div></div>
                <Toggle active={form.favorite || false} onChange={v => set("favorite", v)} />
              </div>
              <div className="pp-toggle-wrap">
                <div><div className="pp-edit-toggle-label">Venta cruzada</div></div>
                <Toggle active={form.crossSell || false} onChange={v => set("crossSell", v)} />
              </div>
              <FG label="Inventario">
                <select className="pp-form-select" value={form.groupType || "infinite"} onChange={e => set("groupType", e.target.value)}>
                  <option value="infinite">No limitado</option>
                  <option value="limited">Limitado</option>
                </select>
              </FG>


              <FG label="Disponible en ecommerce">
                <select className="pp-form-select"  >
                  <option value="infinite">Si</option>
                  <option value="limited">No</option>
                </select>
              </FG>

              <FG label="Disponible en para delivery en plataformas externas">
                <select className="pp-form-select"  >
                  <option value="infinite">Si</option>
                  <option value="limited">No</option>
                </select>
              </FG>
            </div>
            {/* )} */}
            <FG label="Términos y condiciones"><textarea className="pp-form-textarea pp-form-textarea--sm" value={form.terms || ""} onChange={e => set("terms", e.target.value)} placeholder="Escribe los términos y condiciones..." /></FG>
            <FG label="Códigos UPC"><input className="pp-form-input" value={form.upc || ""} onChange={e => set("upc", e.target.value)} placeholder="Ej: 1111111111113" /></FG>
          </div>

          <div className="pp-edit-actions">
            <Btn t="s" onClick={onCancel}>Cancelar</Btn>
            <Btn t="p" onClick={() => onSave(form)}>Guardar</Btn>
          </div>
        </div>
      </div>
    );
  }

  // ── View mode ──
  return (
    <div className="pp-detail-card">
      <div className="pp-detail-card-head">
        <div className="pp-detail-head-left">
          <span className="pp-detail-title" title={product.name}>{product.name}</span>
          {isBranchView && <span className={cx("pp-badge-sm", !product.active && "pp-badge-sm--off")}>{product.active ? "Activo" : "Inactivo"}</span>}
        </div>
        <div className="pp-detail-head-right">
          {isBranchView && (
            <div className="pp-detail-status">
              <span className={cx("pp-detail-status-text", product.active ? "pp-detail-status-text--on" : "pp-detail-status-text--off")}>{product.active ? "Activo" : "Inactivo"}</span>
              <Toggle active={product.active} onChange={onToggleActive} />
            </div>
          )}
          <TranslationsButton entity="product" entityId={getGroupId(product)} title={product.name} compact />
          <button className="pp-btn-icon pp-detail-icon-btn" data-tour="oc-edit" onClick={onToggleEdit}><I.Pencil s={15} /></button>
        </div>
      </div>
      <div className="pp-detail-body">
        <div className="pp-detail-thumb">
          {product.imageUrl ? <img src={product.imageUrl} alt="" /> : product.emoji}
        </div>
        <div className="pp-detail-info">
          <div className="pp-cat-inline-list">
            {assignedCategories.length > 0 ? assignedCategories.map(category => (
              <span key={category.id} className="pp-cat-mini-tag">{category.emoji} {category.name}</span>
            )) : (
              <span className="pp-cat-mini-tag">{catEmoji} {catName}</span>
            )}
          </div>
          {isBranchView && <div className="pp-detail-price">{fmt(product.price)}</div>}
          {product.description && <div className="pp-detail-desc">{product.description}</div>}
        </div>
      </div>
      {/* Config summary */}
      <div className="pp-detail-summary">
        <div className="pp-detail-pill-grid">
          <InfoPill label="Tipo" value={product.type || "normal"} />
          <InfoPill label="Preparación" value={product.preparation === "no" ? "No" : "Sí"} />
          <InfoPill label="Ref. externa" value={product.externalRef || "No tiene"} />
          <InfoPill label="Puntos" value={product.pointsPay ? "Sí" : "No"} />
        </div>
        {product.upc && (
          <div className="pp-detail-upc">
            <I.Barcode />
            <span className="pp-detail-upc-code">{product.upc}</span>
            <span className="pp-detail-upc-tag">UPC</span>
          </div>
        )}
        {product.terms && (
          <TermsPreview terms={product.terms} />
        )}
      </div>
    </div>
  );
}

function InfoPill({ label, value }) {
  return (
    <div className="pp-info-pill">
      <span className="pp-info-pill-label">{label}</span>
      <span className="pp-info-pill-value">{value}</span>
    </div>
  );
}

function TermsPreview({ terms }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="pp-terms-wrap">
      <button className="pp-btn-ghost pp-terms-toggle" onClick={() => setOpen(!open)}>{open ? "Ocultar términos" : "Ver términos y condiciones"}</button>
      {open && <div className="pp-terms-body">{terms}</div>}
    </div>
  );
}

// ─── SEGMENTS TAB ──────────────────────────────────────────────
function SegmentsTab({ product, onUpdate }) {
  const [segments, setSegments] = useState(product.segments || [
    { id: uid(), name: "Tamaño", options: [{ name: "Regular", price: 0 }, { name: "Grande", price: 1.50 }] },
    { id: uid(), name: "Extras", options: [{ name: "Queso extra", price: 0.75 }, { name: "Bacon", price: 1.00 }] },
  ]);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [addingOptionTo, setAddingOptionTo] = useState(null);
  const [newOptName, setNewOptName] = useState("");
  const [newOptPrice, setNewOptPrice] = useState("");

  useEffect(() => {
    setSegments(product.segments || []);
  }, [product.id, product.segments]);

  const addSegment = () => { if (!newName.trim()) return; setSegments(p => [...p, { id: uid(), name: newName.trim(), options: [] }]); setNewName(""); setAdding(false); };
  const addOption = (segId) => {
    if (!newOptName.trim()) return;
    setSegments(p => p.map(s => s.id === segId ? { ...s, options: [...s.options, { name: newOptName.trim(), price: parseFloat(newOptPrice) || 0 }] } : s));
    setNewOptName(""); setNewOptPrice(""); setAddingOptionTo(null);
  };

  return (
    <div>
      <div className="pp-seg-header-row">
        <span className="pp-section-label">Segmentos</span>
        <button className="pp-btn-ghost pp-btn-ghost--sm" onClick={() => setAdding(true)}><I.Plus s={12} /> Agregar</button>
      </div>
      {adding && (
        <div className="pp-seg-add-form">
          <input className="pp-form-input" style={{ marginBottom: 8 }} placeholder="Nombre del segmento" value={newName} onChange={e => setNewName(e.target.value)} autoFocus />
          <div className="pp-flex-end">
            <Btn t="s" onClick={() => setAdding(false)} style={{ padding: "4px 12px", fontSize: 12 }}>Cancelar</Btn>
            <Btn t="p" onClick={addSegment} style={{ padding: "4px 12px", fontSize: 12 }}>Crear</Btn>
          </div>
        </div>
      )}
      {product.segmentsLoading && <div className="pp-seg-loading">Cargando segmentos...</div>}
      {!product.segmentsLoading && segments.map(seg => <SegmentCard key={seg.id} segment={seg} addingOption={addingOptionTo === seg.id} onStartAdd={() => { setAddingOptionTo(seg.id); setNewOptName(""); setNewOptPrice(""); }} onCancelAdd={() => setAddingOptionTo(null)} newOptName={newOptName} setNewOptName={setNewOptName} newOptPrice={newOptPrice} setNewOptPrice={setNewOptPrice} onAddOption={() => addOption(seg.id)} />)}
      {!product.segmentsLoading && segments.length === 0 && !adding && <div className="pp-seg-loading">No hay segmentos.</div>}
    </div>
  );
}

function SegmentCard({ segment, addingOption, onStartAdd, onCancelAdd, newOptName, setNewOptName, newOptPrice, setNewOptPrice, onAddOption }) {
  const [exp, setExp] = useState(true);
  const options = segment.options || [];
  return (
    <div className="pp-seg-card">
      <div className="pp-seg-card-head" onClick={() => setExp(!exp)}>
        <div className="pp-seg-card-head-left">
          <div className={cx("pp-seg-chevron", exp && "pp-seg-chevron--open")}><I.ChevronRight /></div>
          <span className="pp-seg-name" title={segment.name}>{segment.name}</span>
          <span className="pp-seg-count">{options.length}</span>
        </div>
        <button className="pp-btn-icon pp-btn-icon--sm" onClick={e => { e.stopPropagation(); onStartAdd(); }}><I.Plus s={12} /></button>
      </div>
      {exp && (
        <div style={{ padding: segment.description ? "8px 14px 0" : "0 14px" }}>
          {segment.description && <div className="pp-seg-desc">{segment.description}</div>}
          {(segment.minimumCount > 0 || segment.maximumCount > 0) && (
            <div className="pp-seg-meta-row">
              <span className="pp-seg-meta">Min: {segment.minimumCount}</span>
              <span className="pp-seg-meta">Max: {segment.maximumCount}</span>
            </div>
          )}
        </div>
      )}
      {exp && options.length > 0 && (
        <div className="pp-seg-opts">
          {options.map((opt, i) => (
            <div key={opt.id || i} className={cx("pp-seg-opt", i < options.length - 1 && "pp-seg-opt--divider")}>
              <div className="pp-seg-opt-row">
                <span className="pp-seg-opt-name" title={opt.name}>{opt.name}</span>
                <span className={cx("pp-seg-opt-price", opt.price > 0 ? "pp-seg-opt-price--paid" : "pp-seg-opt-price--free")}>{opt.price > 0 ? `+${fmt(opt.price)}` : "Incluido"}</span>
              </div>
              {opt.isDefault && <div className="pp-badge-sm" style={{ display: "inline-flex", marginTop: 5 }}>Por defecto</div>}
              {opt.description && <div className="pp-seg-opt-desc">{opt.description}</div>}
              {opt.taxes?.length > 0 && (
                <div className="pp-seg-opt-taxes">
                  <span className="pp-seg-opt-taxes-label">Impuestos:</span>
                  {opt.taxes.map(tax => <span key={tax.id} className="pp-seg-meta"> {tax.rate}</span>)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {addingOption && (
        <div className="pp-seg-inline-add">
          <div className="pp-seg-inline-add-row">
            <input className="pp-form-input pp-form-input--sm" style={{ flex: 2 }} placeholder="Nombre" value={newOptName} onChange={e => setNewOptName(e.target.value)} autoFocus />
            <input className="pp-form-input pp-form-input--sm" style={{ flex: 1 }} placeholder="Precio" type="number" step="0.01" value={newOptPrice} onChange={e => setNewOptPrice(e.target.value)} />
          </div>
          <div className="pp-flex-end">
            <Btn t="s" onClick={onCancelAdd} style={{ padding: "3px 10px", fontSize: 11 }}>Cancelar</Btn>
            <Btn t="p" onClick={onAddOption} disabled={!newOptName.trim()} style={{ padding: "3px 10px", fontSize: 11 }}>Agregar</Btn>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── LARGE MODAL WRAPPER ───────────────────────────────────────
function ProductPanelLargeModal({ children, onClose, maxWidth = 760 }) {
  return (
    <div className="pp-overlay" onClick={onClose}>
      <div className="pp-modal pp-modal--large" style={{ maxWidth }} onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function ConnectedSegmentsTab({ product, onUpdate }) {
  const [segments, setSegments] = useState(product.segments || []);
  const [showSegmentModal, setShowSegmentModal] = useState(false);
  const [itemModalSegment, setItemModalSegment] = useState(null);
  const [busyKey, setBusyKey] = useState("");
  const token = localStorage.getItem("x-access-token");
  const user = getStoredUser();
  const businessId = getBusinessId(user);
  const groupId = getGroupId(product);

  useEffect(() => {
    setSegments(product.segments || []);
  }, [product.id, product.segments]);

  const updateSegments = (nextSegments) => {
    setSegments(nextSegments);
    onUpdate?.({ ...product, segments: nextSegments, segmentsLoading: false });
  };

  const refreshSegments = async () => {
    if (!groupId) return [];
    const response = await SegmentsFechtAPI.getSegmentsByGroup(groupId, token);
    const nextSegments = asArray(response?.data?.data).map(normalizeSegment);
    updateSegments(nextSegments);
    return nextSegments;
  };

  const resolveCreatedSegmentId = async (response, name) => {
    const data = response?.data?.data || response?.data || {};
    const segmentId = data?.segment_id || data?.id || data?.createdSegmentId || data?.created_segment_id;
    if (segmentId) return segmentId;
    const list = await SegmentsFechtAPI.getSegmentByBusiness(businessId, token);
    const match = asArray(list?.data?.data).find(segment => String(segment?.name || "").trim().toLowerCase() === name.trim().toLowerCase());
    return getSegmentId(match);
  };

  const resolveCreatedItemId = async (response, name) => {
    const data = response?.data?.data || response?.data || {};
    const itemId = data?.item_id || data?.id || data?.createdItemId || data?.created_item_id;
    if (itemId) return itemId;
    const list = await ItemsFechtAPI.getItemsbYBusiness(businessId, token);
    const match = asArray(list?.data?.data).find(item => String(item?.name || "").trim().toLowerCase() === name.trim().toLowerCase());
    return getItemId(match);
  };

  const assignSegment = async (segment) => {
    const segmentId = getSegmentId(segment);
    if (!groupId || !segmentId || segments.some(current => String(getSegmentId(current)) === String(segmentId))) return;
    setBusyKey(`segment-${segmentId}`);
    try {
      await SegmentsFechtAPI.addSegmentToGroup(groupId, segmentId, token);
      await refreshSegments();
      setShowSegmentModal(false);
    } catch (err) {
      console.log(err);
      ErrorAlert(getApiError(err, "No se pudo asignar el segmento."), "Error de asignación", "error");
    } finally {
      setBusyKey("");
    }
  };

  const createSegmentAndAssign = async (data) => {
    setBusyKey("segment-create");
    try {
      const payload = {
        name: data.name,
        description: data.description,
        type: data.type,
        minimumCount: data.minimumCount || 0,
        maximumCount: data.maximumCount || 0,
        external_reference: data.external_reference || "",
        business_id: businessId,
      };
      if (user?.business?.posIntegration !== undefined) payload.pos_integration = user.business.posIntegration;
      const response = await SegmentsFechtAPI.createSegment(payload, token);
      const segmentId = await resolveCreatedSegmentId(response, data.name);
      if (!segmentId) throw new Error("No se pudo identificar el segmento creado.");
      await SegmentsFechtAPI.addSegmentToGroup(groupId, segmentId, token);
      await refreshSegments();
      setShowSegmentModal(false);
    } catch (err) {
      console.log(err);
      ErrorAlert(getApiError(err, "No se pudo crear el segmento."), "Error de registro", "error");
    } finally {
      setBusyKey("");
    }
  };

  const removeSegment = async (segment) => {
    const segmentId = getSegmentId(segment);
    if (!groupId || !segmentId) return;
    setBusyKey(`segment-remove-${segmentId}`);
    try {
      await GroupsFechtAPI.deleteSegmenttoGroup(segmentId, { group_id: groupId }, token);
      await refreshSegments();
    } catch (err) {
      console.log(err);
      ErrorAlert(getApiError(err, "No se pudo remover el segmento."), "Error de segmentos", "error");
    } finally {
      setBusyKey("");
    }
  };

  const assignItemToSegment = async (segment, item, config) => {
    const segmentId = getSegmentId(segment);
    const itemId = getItemId(item);
    if (!segmentId || !itemId) return;
    setBusyKey(`item-${segmentId}-${itemId}`);
    try {
      await SegmentsFechtAPI.asingItemsToSegment(itemId, segmentId, { price: toSegmentPrice(config.price), taxes: config.taxes || [] }, token);
      await refreshSegments();
      setItemModalSegment(null);
    } catch (err) {
      console.log(err);
      ErrorAlert(getApiError(err, "No se pudo asignar el producto individual."), "Error de asignación", "error");
    } finally {
      setBusyKey("");
    }
  };

  const createItemAndAssign = async (segment, data, config) => {
    const segmentId = getSegmentId(segment);
    if (!segmentId) return;
    setBusyKey("item-create");
    try {
      const formData = new FormData();
      formData.append("business_id", businessId);
      formData.append("price", "0");
      formData.append("item_type", "infinite");
      formData.append("name", data.name);
      formData.append("description", data.description);
      formData.append("item_preparation", data.item_preparation);
      formData.append("external_reference", data.external_reference || "");
      if (user?.business?.posIntegration !== undefined) formData.append("pos_integration", user.business.posIntegration);
      if (data.file) formData.append("files", data.file);
      const response = await ItemsFechtAPI.createSingleItem(formData, token);
      const itemId = await resolveCreatedItemId(response, data.name);
      if (!itemId) throw new Error("No se pudo identificar el producto individual creado.");
      await SegmentsFechtAPI.asingItemsToSegment(itemId, segmentId, { price: toSegmentPrice(config.price), taxes: config.taxes || [] }, token);
      await refreshSegments();
      setItemModalSegment(null);
    } catch (err) {
      console.log(err);
      ErrorAlert(getApiError(err, "No se pudo crear el producto individual."), "Error de registro", "error");
    } finally {
      setBusyKey("");
    }
  };

  const removeItemFromSegment = async (segment, item) => {
    const segmentId = getSegmentId(segment);
    const itemId = getItemId(item);
    if (!segmentId || !itemId) return;
    setBusyKey(`item-remove-${segmentId}-${itemId}`);
    try {
      await SegmentsFechtAPI.deleteItemtoSegment(segmentId, { item_id: itemId }, token);
      await refreshSegments();
    } catch (err) {
      console.log(err);
      ErrorAlert(getApiError(err, "No se pudo remover el producto individual."), "Error de segmentos", "error");
    } finally {
      setBusyKey("");
    }
  };

  const toggleDefaultItem = async (segment, item) => {
    const segmentId = getSegmentId(segment);
    const itemId = getItemId(item);
    if (!segmentId || !itemId || !groupId) return;
    setBusyKey(`default-${segmentId}-${itemId}`);
    try {
      if (item.isDefault) {
        await GroupsFechtAPI.removeDefaultItemTosegmentOnGroup(segmentId, groupId, { item_id_segment_default_to_remove: [itemId] }, token);
      } else {
        await GroupsFechtAPI.asingDefaultItemTosegmentOnGroup(segmentId, groupId, { item_id_segment_default: [itemId] }, token);
      }
      await refreshSegments();
    } catch (err) {
      console.log(err);
      ErrorAlert(getApiError(err, "No se pudo actualizar el item por defecto."), "Error de segmentos", "error");
    } finally {
      setBusyKey("");
    }
  };

  return (
    <div>
      <div className="pp-seg-header-row" data-tour="oc-segments">
        <span className="pp-section-label">Modificadores</span>
        <button className="pp-btn-ghost pp-btn-ghost--sm" onClick={() => setShowSegmentModal(true)}><I.Plus s={12} /> Agregar</button>
      </div>
      {product.segmentsLoading && <div className="pp-seg-loading">Cargando segmentos...</div>}
      {!product.segmentsLoading && segments.map(seg => (
        <ConnectedSegmentCard
          key={seg.id}
          segment={seg}
          busyKey={busyKey}
          onOpenItems={() => setItemModalSegment(seg)}
          onRemoveSegment={() => removeSegment(seg)}
          onRemoveItem={(item) => removeItemFromSegment(seg, item)}
          onToggleDefault={(item) => toggleDefaultItem(seg, item)}
        />
      ))}
      {!product.segmentsLoading && segments.length === 0 && <div className="pp-seg-loading">No hay segmentos asignados.</div>}
      {showSegmentModal && <SegmentLibraryModal assignedSegments={segments} businessId={businessId} busyKey={busyKey} onClose={() => setShowSegmentModal(false)} onAssign={assignSegment} onCreate={createSegmentAndAssign} />}
      {itemModalSegment && <ItemLibraryModal segment={itemModalSegment} businessId={businessId} busyKey={busyKey} onClose={() => setItemModalSegment(null)} onAssign={(item, config) => assignItemToSegment(itemModalSegment, item, config)} onCreate={(data, config) => createItemAndAssign(itemModalSegment, data, config)} />}
    </div>
  );
}

function ConnectedSegmentCard({ segment, busyKey, onOpenItems, onRemoveSegment, onRemoveItem, onToggleDefault }) {
  const [exp, setExp] = useState(true);
  const options = segment.options || [];
  return (
    <div className="pp-seg-card">
      <div className="pp-seg-card-head" onClick={() => setExp(!exp)}>
        <div className="pp-seg-card-head-left">
          <div className={cx("pp-seg-chevron", exp && "pp-seg-chevron--open")}><I.ChevronRight /></div>
          <span className="pp-seg-name" title={segment.name}>{segment.name}</span>
          <span className="pp-seg-count">{options.length}</span>
        </div>
        <div className="pp-seg-head-actions">
          <TranslationsButton entity="segment" entityId={getSegmentId(segment)} title={segment.name} compact />
          <button className="pp-btn-icon pp-btn-icon--sm" onClick={e => { e.stopPropagation(); onOpenItems(); }} title="Asignar producto individual"><I.Plus s={12} /></button>
          <button className="pp-btn-icon pp-btn-icon--sm pp-danger" disabled={busyKey === `segment-remove-${getSegmentId(segment)}`} onClick={e => { e.stopPropagation(); onRemoveSegment(); }} title="Remover segmento"><I.Trash /></button>
        </div>
      </div>

      {exp && (
        <div className="pp-seg-add-wrap">
          <button className="pp-seg-add-item" onClick={onOpenItems}>
            <I.Plus s={15} /> Asignar producto individual
          </button>
        </div>
      )}
      {exp && (
        <div style={{ padding: segment.description ? "8px 14px 0" : "0 14px" }}>
          {segment.description && <div className="pp-seg-desc">{segment.description}</div>}
          {(segment.minimumCount > 0 || segment.maximumCount > 0) && (
            <div className="pp-seg-meta-row">
              <span className="pp-seg-meta">Min: {segment.minimumCount}</span>
              <span className="pp-seg-meta">Max: {segment.maximumCount}</span>
            </div>
          )}
        </div>
      )}
      {exp && options.length > 0 && (
        <div className="pp-seg-opts ">
          {options.map((opt, i) => (
            <div key={opt.id || i} className={cx("pp-seg-opt mb-3", i < options.length - 1 && "pp-seg-opt--divider")}>
              <div className="pp-seg-opt-row">
                <span className="pp-seg-opt-name" title={opt.name}>
                  <TranslationsButton entity="item" entityId={getItemId(opt)} title={opt.name} compact />
                  {opt.name}</span>
                <span className={cx("pp-seg-opt-price", opt.price > 0 ? "pp-seg-opt-price--paid" : "pp-seg-opt-price--free")}>{opt.price > 0 ? `+${fmt(opt.price)}` : "Incluido"}</span>
              </div>
              <div className="pp-seg-opt-actions d-none">
                <button className={cx("pp-seg-action", opt.isDefault && "pp-seg-action--active")} disabled={busyKey === `default-${getSegmentId(segment)}-${getItemId(opt)}`} onClick={() => onToggleDefault(opt)}>{opt.isDefault ? "Por defecto" : "Hacer default"}</button>
                <button className="pp-seg-action pp-danger" disabled={busyKey === `item-remove-${getSegmentId(segment)}-${getItemId(opt)}`} onClick={() => onRemoveItem(opt)}>Remover</button>
              </div>
              {opt.description && <div className="pp-seg-opt-desc">{opt.description}</div>}

              {opt.taxes?.length > 0 && (
                <div className="pp-seg-opt-taxes">
                  <span className="pp-seg-opt-taxes-label">Impuestos:</span>
                  {opt.taxes.map(tax => <span key={tax.id} className="pp-seg-meta"> {tax.rate}</span>)}
                </div>
              )}

              <div className="pp-seg-opt-actions">


                <button className={cx("pp-seg-action", opt.isDefault && "pp-seg-action--active")} disabled={busyKey === `default-${getSegmentId(segment)}-${getItemId(opt)}`} onClick={() => onToggleDefault(opt)}>{opt.isDefault ? "Por defecto" : "Establecer por defecto"}</button>
                <button className="pp-seg-action pp-danger" disabled={busyKey === `item-remove-${getSegmentId(segment)}-${getItemId(opt)}`} onClick={() => onRemoveItem(opt)}>Remover</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SegmentLibraryModal({ assignedSegments, businessId, busyKey, onClose, onAssign, onCreate }) {
  const [segments, setSegments] = useState([]);
  const [segmentTypes, setSegmentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const token = localStorage.getItem("x-access-token");
  const assignedIds = assignedSegments.map(segment => String(getSegmentId(segment)));

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [segmentsResponse, typesResponse] = await Promise.allSettled([
          SegmentsFechtAPI.getSegmentByBusiness(businessId, token),
          SegmentsFechtAPI.getSegmentTypes(token),
        ]);
        setSegments(segmentsResponse.status === "fulfilled" ? asArray(segmentsResponse.value?.data?.data).map(normalizeSegment) : []);
        setSegmentTypes(typesResponse.status === "fulfilled" ? asArray(typesResponse.value?.data?.segment_types).map(type => ({ id: type.id, name: type.name })) : []);
      } catch (err) {
        console.log(err);
        ErrorAlert(getApiError(err, "No se pudieron cargar los segmentos."), "Error de segmentos", "error");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [businessId, token]);

  return (
    <ProductPanelLargeModal onClose={onClose} maxWidth={760}>
      <div className="pp-lib-header">
        <div>
          <h3 className="pp-lib-title">Asignar segmento</h3>
          <div className="pp-lib-subtitle">Elige un segmento existente o crea uno nuevo para este producto.</div>
        </div>
        <button className="pp-btn-icon" onClick={onClose}><I.X /></button>
      </div>
      <div className="pp-lib-toolbar">
        <Btn t={creating ? "s" : "p"} onClick={() => setCreating(false)}>Existentes</Btn>
        <Btn t={creating ? "p" : "s"} onClick={() => setCreating(true)}><I.Plus /> Nuevo segmento</Btn>
      </div>
      <div className="pp-lib-body">
        {creating ? (
          <SegmentCreateForm segmentTypes={segmentTypes} saving={busyKey === "segment-create"} onCreate={onCreate} onCancel={() => setCreating(false)} />
        ) : loading ? (
          <div className="pp-lib-empty">Cargando segmentos...</div>
        ) : segments.length === 0 ? (
          <div className="pp-lib-empty">No hay segmentos creados.</div>
        ) : (
          <div className="pp-lib-grid">
            {segments.map(segment => {
              const segmentId = String(getSegmentId(segment));
              const assigned = assignedIds.includes(segmentId);
              return (
                <button key={segment.id} className={cx("pp-lib-card", assigned && "pp-lib-card--disabled")} disabled={assigned || busyKey === `segment-${segmentId}`} onClick={() => onAssign(segment)}>
                  <div className="pp-lib-card-head">
                    <span className="pp-lib-card-title">{segment.name}</span>
                    <span className="pp-seg-meta">{assigned ? "Asignado" : "Asignar"}</span>
                  </div>
                  {segment.description && <div className="pp-lib-card-desc">{segment.description}</div>}
                  <div className="pp-lib-meta-row">
                    <span className="pp-seg-meta">Min: {segment.minimumCount || 0}</span>
                    <span className="pp-seg-meta">Max: {segment.maximumCount || 0}</span>
                    <span className="pp-seg-meta">{segment.options?.length || 0} items</span>
                  </div>
                  {segment.options?.length > 0 && (
                    <div className="pp-lib-preview-list">
                      {segment.options.slice(0, 3).map(item => <span key={item.id} className="pp-lib-preview-chip">{item.name}</span>)}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </ProductPanelLargeModal>
  );
}

function SegmentCreateForm({ segmentTypes, saving, onCreate, onCancel }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState(segmentTypes[0]?.id || "");
  const [minimumCount, setMinimumCount] = useState("0");
  const [maximumCount, setMaximumCount] = useState("1");
  const [externalRef, setExternalRef] = useState("");

  useEffect(() => {
    if (!type && segmentTypes[0]?.id) setType(segmentTypes[0].id);
  }, [segmentTypes, type]);

  return (
    <div className="pp-create-panel">
      <FG label="Nombre"><input className="pp-form-input" value={name} onChange={e => setName(e.target.value)} placeholder="Nombre del segmento" autoFocus /></FG>
      <FG label="Descripción"><textarea className="pp-form-textarea" value={description} onChange={e => setDescription(e.target.value)} placeholder="Descripción del segmento" /></FG>
      <div className="pp-edit-grid-2">
        <FG label="Tipo">
          <select className="pp-form-select" value={type} onChange={e => setType(e.target.value)}>
            <option value="">Seleccione...</option>
            {segmentTypes.map(segmentType => <option key={segmentType.id} value={segmentType.id}>{segmentType.name}</option>)}
          </select>
        </FG>
        <FG label="Referencia externa"><input className="pp-form-input" value={externalRef} onChange={e => setExternalRef(e.target.value)} placeholder="Opcional" /></FG>
      </div>
      <div className="pp-edit-grid-2">
        <FG label="Cantidad mínima"><input className="pp-form-input" type="number" min="0" value={minimumCount} onChange={e => setMinimumCount(e.target.value)} /></FG>
        <FG label="Cantidad máxima"><input className="pp-form-input" type="number" min="0" value={maximumCount} onChange={e => setMaximumCount(e.target.value)} /></FG>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Btn t="s" onClick={onCancel} disabled={saving}>Cancelar</Btn>
        <Btn t="p" disabled={saving || !name.trim() || !description.trim()} onClick={() => onCreate({ name: name.trim(), description: description.trim(), type, minimumCount, maximumCount, external_reference: externalRef })}>{saving ? "Creando..." : "Crear y asignar"}</Btn>
      </div>
    </div>
  );
}

function ItemLibraryModal({ segment, businessId, busyKey, onClose, onAssign, onCreate }) {
  const [items, setItems] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState("");
  const [price, setPrice] = useState("0");
  const [selectedTaxIds, setSelectedTaxIds] = useState([]);
  const token = localStorage.getItem("x-access-token");
  const assignedItemIds = asArray(segment.options).map(item => String(getItemId(item)));

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [itemsResponse, taxesResponse] = await Promise.allSettled([
          ItemsFechtAPI.getItemsbYBusiness(businessId, token),
          TaxesFetchAPI.getTaxes(token),
        ]);
        setItems(itemsResponse.status === "fulfilled" ? asArray(itemsResponse.value?.data?.data).map((item, index) => normalizeSegmentOption(item, index)) : []);
        setTaxes(taxesResponse.status === "fulfilled" ? asArray(taxesResponse.value?.data?.data).map(normalizeTax) : []);
      } catch (err) {
        console.log(err);
        ErrorAlert(getApiError(err, "No se pudieron cargar los productos individuales."), "Error de items", "error");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [businessId, token]);

  const toggleTax = (taxId) => {
    setSelectedTaxIds(ids => ids.includes(taxId) ? ids.filter(id => id !== taxId) : [...ids, taxId]);
  };
  const selectedItem = items.find(item => String(getItemId(item)) === String(selectedItemId));
  const assignmentConfig = { price: toSegmentPrice(price), taxes: selectedTaxIds.map(id => Number(id) || id) };
  const canAssign = Boolean(selectedItem) && selectedTaxIds.length > 0 && !busyKey.startsWith("item-");

  return (
    <ProductPanelLargeModal onClose={onClose} maxWidth={780}>
      <div className="pp-lib-header">
        <div>
          <h3 className="pp-lib-title">Asignar producto individual</h3>
          <div className="pp-lib-subtitle">{segment.name}</div>
        </div>
        <button className="pp-btn-icon" onClick={onClose}><I.X /></button>
      </div>
      <div className="pp-lib-toolbar">
        <Btn t={creating ? "s" : "p"} onClick={() => setCreating(false)}>Existentes</Btn>
        <Btn t={creating ? "p" : "s"} onClick={() => setCreating(true)}><I.Plus /> Nuevo individual</Btn>
      </div>
      <div className="pp-lib-body">
        {creating ? (
          <ItemCreateForm taxes={taxes} selectedTaxIds={selectedTaxIds} onToggleTax={toggleTax} price={price} setPrice={setPrice} saving={busyKey === "item-create"} onCancel={() => setCreating(false)} onCreate={(data) => onCreate(data, assignmentConfig)} />
        ) : loading ? (
          <div className="pp-lib-empty">Cargando productos individuales...</div>
        ) : (
          <>
            <div className="pp-assign-panel pp-assign-panel--flush">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1.3fr", gap: 10 }}>
                <FG label="Precio en el segmento"><input className="pp-form-input" type="number" step="0.01" value={price} onChange={e => setPrice(e.target.value)} /></FG>
                <FG label="Impuestos"><TaxPicker taxes={taxes} selectedTaxIds={selectedTaxIds} onToggle={toggleTax} /></FG>
              </div>
              <div className="pp-assign-panel-foot">
                <span className={cx("pp-assign-hint", selectedTaxIds.length === 0 && "pp-assign-hint--error")}>{selectedTaxIds.length > 0 ? "Selecciona el item y confirma la asignación." : "Selecciona al menos un impuesto para asignar."}</span>
                <Btn t="p" disabled={!canAssign} onClick={() => onAssign(selectedItem, assignmentConfig)}>Asignar producto</Btn>
              </div>
            </div>
            <div className="pp-lib-grid">
              {items.map(item => {
                const itemId = String(getItemId(item));
                const assigned = assignedItemIds.includes(itemId);
                const selected = String(selectedItemId) === itemId;
                return (
                  <button key={item.id} className={cx("pp-lib-item-card", selected && "pp-lib-item-card--selected", assigned && "pp-lib-card--disabled")} disabled={assigned} onClick={() => setSelectedItemId(itemId)}>
                    {selected && <span className="pp-lib-item-check"><I.Check /></span>}
                    <div className="pp-lib-item-img">{item.imageUrl ? <img src={item.imageUrl} alt="" /> : <I.Package />}</div>
                    <div className="pp-lib-item-body">
                      <div className="pp-lib-card-title">{item.name}</div>
                      {item.description && <div className="pp-lib-card-desc">{item.description}</div>}
                      {assigned && <span className="pp-seg-meta">Ya asignado</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </ProductPanelLargeModal>
  );
}

function ItemCreateForm({ taxes, selectedTaxIds, onToggleTax, price, setPrice, saving, onCancel, onCreate }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [preparation, setPreparation] = useState("0");
  const [externalRef, setExternalRef] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");

  const pickFile = (nextFile) => {
    setFile(nextFile || null);
    setPreview(nextFile ? URL.createObjectURL(nextFile) : "");
  };

  return (
    <div className="pp-create-panel">
      {preview && <div className="pp-item-preview-img"><img src={preview} alt="" /></div>}
      <FG label="Nombre"><input className="pp-form-input" value={name} onChange={e => setName(e.target.value)} placeholder="Nombre del producto" autoFocus /></FG>
      <FG label="Descripción"><textarea className="pp-form-textarea" value={description} onChange={e => setDescription(e.target.value)} placeholder="Descripción del producto" /></FG>
      <div className="pp-edit-grid-2">
        <FG label="Preparación">
          <select className="pp-form-select" value={preparation} onChange={e => setPreparation(e.target.value)}>
            <option value="0">No</option>
            <option value="1">Sí</option>
          </select>
        </FG>
        <FG label="Referencia externa"><input className="pp-form-input" value={externalRef} onChange={e => setExternalRef(e.target.value)} placeholder="Opcional" /></FG>
      </div>
      <FG label="Imagen"><input className="pp-form-input" type="file" accept="image/*" onChange={e => pickFile(e.target.files?.[0])} /></FG>
      <div className="pp-assign-panel">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1.3fr", gap: 10 }}>
          <FG label="Precio en el segmento"><input className="pp-form-input" type="number" step="0.01" value={price} onChange={e => setPrice(e.target.value)} /></FG>
          <FG label="Impuestos"><TaxPicker taxes={taxes} selectedTaxIds={selectedTaxIds} onToggle={onToggleTax} /></FG>
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Btn t="s" onClick={onCancel} disabled={saving}>Cancelar</Btn>
        <Btn t="p" disabled={saving || !name.trim() || !description.trim() || !file || selectedTaxIds.length === 0} onClick={() => onCreate({ name: name.trim(), description: description.trim(), item_preparation: preparation, external_reference: externalRef, file })}>{saving ? "Creando..." : "Crear y asignar"}</Btn>
      </div>
    </div>
  );
}

function TaxPicker({ taxes, selectedTaxIds, onToggle }) {
  if (taxes.length === 0) return <div className="pp-tax-empty">Sin impuestos disponibles</div>;
  return (
    <div className="pp-tax-choice-list">
      {taxes.map(tax => {
        const taxId = String(getTaxId(tax));
        const selected = selectedTaxIds.includes(taxId);
        return (
          <button key={taxId} type="button" className={cx("pp-tax-choice", "pp-tax-choice--sm", selected && "pp-tax-choice--active")} onClick={() => onToggle(taxId)}>
            <span>{tax.name}</span>
            <span className={cx("pp-tax-choice-rate", selected && "pp-tax-choice-rate--active")}>{tax.rate}</span>
          </button>
        );
      })}
    </div>
  );
}

function TaxesTab({ product, onUpdate }) {
  const [taxes, setTaxes] = useState(product.taxes || []);
  const [availableTaxes, setAvailableTaxes] = useState([]);
  const [showList, setShowList] = useState(false);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem("x-access-token");
  const groupId = getGroupId(product);
  const availableToAdd = availableTaxes.filter(t => !taxes.find(x => String(getTaxId(x)) === String(getTaxId(t))));

  useEffect(() => {
    setTaxes(product.taxes || []);
  }, [product.id, product.taxes]);

  const loadTaxes = async () => {
    try {
      setLoading(true);
      const response = await TaxesFetchAPI.getTaxes(token);
      setAvailableTaxes(asArray(response?.data?.data).map(normalizeTax));
    } catch (err) {
      console.log(err);
      setAvailableTaxes([]);
      ErrorAlert(getApiError(err, "No se pudieron cargar los impuestos."), "Error de impuestos", "error");
    } finally {
      setLoading(false);
    }
  };

  const toggleList = async () => {
    const next = !showList;
    setShowList(next);
    if (next && availableTaxes.length === 0) {
      await loadTaxes();
    }
  };

  const addTax = async (tax) => {
    if (!groupId) return;
    try {
      setLoading(true);
      await TaxesFetchAPI.asingTaxToGroup({ tax_id: getTaxId(tax), group_id: groupId }, token);
      const nextTaxes = [...taxes, tax];
      setTaxes(nextTaxes);
      onUpdate({ ...product, taxes: nextTaxes });
      setShowList(false);
      ErrorAlert("", "Impuesto asignado correctamente", "success");
    } catch (err) {
      console.log(err);
      ErrorAlert(getApiError(err, "No se pudo asignar el impuesto."), "Error de asignación", "error");
    } finally {
      setLoading(false);
    }
  };

  const removeTax = async (id) => {
    if (!groupId) return;
    try {
      setLoading(true);
      await TaxesFetchAPI.deleteTaxByGroupId({ tax_id: id, group_id: groupId }, token);
      const nextTaxes = taxes.filter(t => String(getTaxId(t)) !== String(id));
      setTaxes(nextTaxes);
      onUpdate({ ...product, taxes: nextTaxes });
      ErrorAlert("", "Impuesto removido correctamente", "success");
    } catch (err) {
      console.log(err);
      ErrorAlert(getApiError(err, "No se pudo remover el impuesto."), "Error de impuestos", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="pp-section-header">
        <span className="pp-section-label">Impuestos aplicados</span>
        <button className="pp-btn-ghost pp-btn-ghost--sm" disabled={loading || !groupId} onClick={toggleList}><I.Plus s={12} /> Agregar</button>
      </div>

      {/* Dropdown list to add taxes */}
      {showList && (
        <div className="pp-taxlist-add">
          {loading ? (
            <div className="pp-taxlist-add-empty">Cargando impuestos...</div>
          ) : availableToAdd.length === 0 ? (
            <div className="pp-taxlist-add-empty">Todos los impuestos ya están agregados.</div>
          ) : availableToAdd.map(t => (
            <button key={t.id} className="pp-taxlist-add-row" onClick={() => addTax(t)}>
              <span className="pp-taxlist-add-name">{t.name}</span>
              <span className="pp-taxlist-add-rate">{t.rate}</span>
            </button>
          ))}
        </div>
      )}

      {/* Current taxes */}
      <div className="pp-taxlist">
        {taxes.length === 0 ? (
          <div className="pp-taxlist-empty">Sin impuestos aplicados.</div>
        ) : taxes.map((t, i) => (
          <div key={t.id} className={cx("pp-taxlist-row", i < taxes.length - 1 && "pp-taxlist-row--divider")}>
            <span className="pp-taxlist-row-name">{t.name}</span>
            <div className="pp-taxlist-row-right">
              <span className="pp-taxlist-row-rate">{t.rate}</span>
              <button className="pp-btn-icon pp-btn-icon--xs pp-danger" disabled={loading} onClick={() => removeTax(getTaxId(t))}><I.X s={12} /></button>
            </div>
          </div>
        ))}
      </div>

      {/* Categories section 
      <div style={{ marginTop: 20 }}>
        <span className="pp-section-label">Categorías</span>
        <div style={{ borderRadius: 10, border: "1px solid #E8E8EC", background: "#FFF", overflow: "hidden", marginTop: 10 }}>
          {["Clanes Don Lee", "Bebidas"].map((c, i) => (
            <div key={i} style={{ padding: "10px 14px", borderBottom: i < 1 ? "1px solid #F0F0F4" : "none", display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 22, height: 22, borderRadius: 6, background: "#F0F0F5", display: "flex", alignItems: "center", justifyContent: "center" }}><I.Tag /></div>
              <span style={{ fontSize: 12, fontWeight: 500 }}>{c}</span>
            </div>
          ))}
        </div>
      </div> */}

      {/* UPC */}
      <div className="pp-upc-header">
        <I.Barcode /><span className="pp-section-label">Códigos UPC</span>
      </div>
      <div className="pp-upc-box">
        <div className="pp-upc-row">
          <span className="pp-upc-code">{product.upc || "No tiene"}</span>
          <span className="pp-upc-tag">UPC-A</span>
        </div>
      </div>
    </div>
  );
}

// ─── MODALS ────────────────────────────────────────────────────
function Overlay({ children, onClose }) {
  return <div className="pp-overlay" onClick={onClose}><div className="pp-modal" onClick={e => e.stopPropagation()}>{children}</div></div>;
}

const EMOJIS_CAT = ["📂", "🍔", "🍕", "🥗", "🍣", "🍰", "☕", "🥤", "🍺", "🛒", "⭐", "🔥"];
const EMOJIS_PROD = ["🍽️", "🍔", "🍕", "🌮", "🥗", "🍣", "🥩", "🍰", "☕", "🥤", "🍺", "🧃"];

function EmojiPicker({ emojis, selected, onSelect }) {
  return <div className="pp-emoji-grid">{emojis.map(e => <button key={e} onClick={() => onSelect(e)} className={cx("pp-emoji-btn", selected === e && "pp-emoji-btn--selected")}>{e}</button>)}</div>;
}

function ConfirmDeleteModal({ productName, onCancel, onConfirm }) {
  return (
    <Overlay onClose={onCancel}>
      <div className="pp-confirm">
        <div className="pp-confirm-icon"><I.Trash /></div>
        <h3 className="pp-confirm-title">Eliminar producto</h3>
        <p className="pp-confirm-text">
          ¿Seguro que deseas eliminar <strong>{productName}</strong>? Esta acción no se puede deshacer.
        </p>
      </div>
      <div className="pp-modal-footer">
        <Btn t="s" onClick={onCancel}>Cancelar</Btn>
        <button className="pp-btn-danger-solid" onClick={onConfirm}><I.Trash /> Sí, eliminar</button>
      </div>
    </Overlay>
  );
}

function WelcomeModal({ onCreateCategory, onImport, onClose }) {
  return (
    <Overlay onClose={onClose}>
      <div className="pp-welcome-head">
        <div className="pp-welcome-icon"><I.Sparkles /></div>
        <h2 className="pp-welcome-title">Bienvenido a tu panel</h2>
        <p className="pp-welcome-sub">Configura tu menú de productos para Ecommerce y POS.</p>
      </div>
      <div className="pp-welcome-list">
        {[["Crea categorías", <I.Tag key="t" />], ["Agrega productos", <I.Package key="p" />], ["Importa desde Excel", <I.FileSpreadsheet key="f" />]].map(([t, ic], i) => (
          <div key={i} className="pp-welcome-item">
            <div className="pp-welcome-item-icon">{ic}</div>{t}
          </div>
        ))}
      </div>
      <div className="pp-modal-footer"><Btn t="s" onClick={onImport}><I.Upload /> Importar</Btn><Btn t="p" onClick={onCreateCategory}><I.Plus /> Crear categoría</Btn></div>
    </Overlay>
  );
}

function CategoryModal({ onSave, onClose }) {
  const [name, setName] = useState("");
  const [emoji, setEmoji] = useState("📂");
  const [categoryTypes, setCategoryTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem("x-access-token");
  const categoryTypeId = categoryTypes[0]?.id || "";

  const [bgColor, setBgColor] = useState("#1B3A6B");
  const [textColor, setTextColor] = useState("#FFFFFF");

  useEffect(() => {
    const loadCategoryTypes = async () => {
      try {
        setLoadingTypes(true);
        const response = await CategoriesDashFetchAPI.getCategoryType(token);
        setCategoryTypes(asArray(response?.data?.categorie_types));
      } catch (err) {
        console.log(err);
        setCategoryTypes([]);
        ErrorAlert(getApiError(err, "No se pudieron cargar los tipos de categoría."), "Error de categoría", "error");
      } finally {
        setLoadingTypes(false);
      }
    };

    loadCategoryTypes();
  }, [token]);

  const submit = async () => {
    if (!categoryTypeId) {
      ErrorAlert("No hay tipos de categoría disponibles.", "Error de registro", "error");
      return;
    }

    setSaving(true);
    const ok = await onSave({ id: uid(), name: name.trim(), emoji, description: name.trim(), categoryTypeId });
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <>
      <Overlay onClose={onClose}>
        <div className="pp-modal-pad--head"><h3 className="pp-modal-title">Nueva categoría</h3></div>
        <div className="pp-modal-pad">
          <FG label="Ícono"><EmojiPicker emojis={EMOJIS_CAT} selected={emoji} onSelect={setEmoji} /></FG>
          <FG label="Nombre"><input className="pp-form-input" placeholder="Ej: Hamburguesas..." value={name} onChange={e => setName(e.target.value)} autoFocus /></FG>

          <FG label="Colores ">
            <div className="pp-color-row">
              {/* Vista previa en vivo */}
              <div className="pp-color-preview" style={{ background: bgColor, color: textColor }}>
                <span className="pp-color-preview-emoji">{emoji || "🍽️"}</span>
                <span className="pp-color-preview-name">{name.trim() || "Categoría"}</span>
              </div>

              {/* Selectores */}
              <div className="pp-color-fields">
                <label className="pp-color-field">
                  <span className="pp-color-field-label">Fondo</span>
                  <input type="color" className="pp-color-input" value={bgColor} onChange={e => setBgColor(e.target.value)} aria-label="Color de fondo" />
                </label>
                <label className="pp-color-field">
                  <span className="pp-color-field-label">Letra</span>
                  <input type="color" className="pp-color-input" value={textColor} onChange={e => setTextColor(e.target.value)} aria-label="Color de letra" />
                </label>
              </div>


            </div>
          </FG>
          <div className="pp-modal-note">
            <svg className="pp-modal-note-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            <p className="pp-modal-note-text">
              Estos colores se aplicarán en la tarjeta de los productos de esta categoría en el menú del POS. El fondo y la letra que elijas son los que verán los cajeros al vender.
            </p>
          </div>
        </div>
        <div className="pp-modal-footer"><Btn t="s" onClick={onClose} disabled={saving}>Cancelar</Btn><Btn t="p" disabled={saving || loadingTypes || !name.trim() || !categoryTypeId} onClick={submit}>{saving ? "Creando..." : "Crear"}</Btn></div>
      </Overlay>

      {/* <Overlay onClose={onClose}>
        <div className="pp-modal-pad--head"><h3 className="pp-modal-title">Nueva categoría</h3></div>
        <div className="pp-modal-pad">
          <FG label="Ícono"><EmojiPicker emojis={EMOJIS_CAT} selected={emoji} onSelect={setEmoji} /></FG>
          <FG label="Nombre"><input className="pp-form-input" placeholder="Ej: Hamburguesas..." value={name} onChange={e => setName(e.target.value)} autoFocus /></FG>
        </div>
        <div className="pp-modal-footer"><Btn t="s" onClick={onClose} disabled={saving}>Cancelar</Btn><Btn t="p" disabled={saving || loadingTypes || !name.trim() || !categoryTypeId} onClick={submit}>{saving ? "Creando..." : "Crear"}</Btn></div>
      </Overlay> */}

    </>

  );
}

function CategoryEditModal({ category, isBranchView, onSave, onClose }) {
  const [name, setName] = useState(category.name);
  const [emoji, setEmoji] = useState(category.emoji);
  const [desc, setDesc] = useState(category.description || "");
  const [availableEcommerce, setAvailableEcommerce] = useState(category.availableEcommerce || false);
  const [active, setActive] = useState(category.active !== false);

  const [bgColor, setBgColor] = useState("#1B3A6B");
  const [textColor, setTextColor] = useState("#FFFFFF");
  return (
    <Overlay onClose={onClose}>
      <div className="pp-modal-pad--head" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <h3 className="pp-modal-title">Editar categoría</h3>
        <TranslationsButton entity="category" entityId={category.id} title={category.name} />
      </div>
      <div className="pp-modal-pad">
        <div className="pp-upload-zone" style={{ marginBottom: 16, height: 100 }}><span className="pp-upload-emoji-xl">{emoji}</span><span style={{ fontSize: 11, color: "#9090A0" }}>Cambiar imagen</span></div>
        <FG label="Ícono"><EmojiPicker emojis={EMOJIS_CAT} selected={emoji} onSelect={setEmoji} /></FG>
        <FG label="Nombre"><input className="pp-form-input" value={name} onChange={e => setName(e.target.value)} autoFocus /></FG>
        <FG label="Descripción"><textarea className="pp-form-textarea" value={desc} onChange={e => setDesc(e.target.value)} placeholder="Describe la categoría..." /></FG>
        {isBranchView && (
          <>
            <FG label="Categoría activa en esta sucursal">
              <div><div className="pp-edit-toggle-label">{active ? "Activo" : "Inactivo"}</div></div>
              <Toggle active={active} onChange={setActive} />
            </FG>
            <FG label="Disponible en Ecommerce">
              <div><div className="pp-edit-toggle-label">{availableEcommerce ? "Activo" : "Inactivo"}</div></div>
              <Toggle active={availableEcommerce} onChange={setAvailableEcommerce} />
            </FG>
          </>
        )}
        <FG label="Colores ">
          <div className="pp-color-row">
            {/* Vista previa en vivo */}
            <div className="pp-color-preview" style={{ background: bgColor, color: textColor }}>
              <span className="pp-color-preview-emoji">{emoji || "🍽️"}</span>
              <span className="pp-color-preview-name">{name.trim() || "Categoría"}</span>
            </div>

            {/* Selectores */}
            <div className="pp-color-fields">
              <label className="pp-color-field">
                <span className="pp-color-field-label">Fondo</span>
                <input type="color" className="pp-color-input" value={bgColor} onChange={e => setBgColor(e.target.value)} aria-label="Color de fondo" />
              </label>
              <label className="pp-color-field">
                <span className="pp-color-field-label">Letra</span>
                <input type="color" className="pp-color-input" value={textColor} onChange={e => setTextColor(e.target.value)} aria-label="Color de letra" />
              </label>
            </div>


          </div>
        </FG>
      </div>
      <div className="pp-modal-footer"><Btn t="s" onClick={onClose}>Cancelar</Btn><Btn t="p" disabled={!name.trim()} onClick={() => onSave({ ...category, name: name.trim(), emoji, description: desc, availableEcommerce, active })}>Guardar</Btn></div>
    </Overlay>
  );
}

function ProductModal({ categories, isBranchView, onSave, onClose }) {
  const [name, setName] = useState("");
  const [categoryId, setCategoryId] = useState(categories[0]?.id || "");
  const [price, setPrice] = useState("");
  const [emoji, setEmoji] = useState("🍽️");
  const [imageUrl, setImageUrl] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [availableTaxes, setAvailableTaxes] = useState([]);
  const [selectedTaxIds, setSelectedTaxIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [taxesLoading, setTaxesLoading] = useState(false);
  const imageInputRef = useRef(null);
  const useImage = Boolean(imageUrl);
  const token = localStorage.getItem("x-access-token");

  useEffect(() => {
    const loadTaxes = async () => {
      try {
        setTaxesLoading(true);
        const response = await TaxesFetchAPI.getTaxes(token);
        setAvailableTaxes(asArray(response?.data?.data).map(normalizeTax));
      } catch (err) {
        console.log(err);
        setAvailableTaxes([]);
        ErrorAlert(getApiError(err, "No se pudieron cargar los impuestos."), "Error de impuestos", "error");
      } finally {
        setTaxesLoading(false);
      }
    };

    loadTaxes();
  }, [token]);

  const pickImage = (file) => {
    if (!file) return;
    setImageFile(file);
    setImageUrl(URL.createObjectURL(file));
  };
  const toggleTax = (taxId) => {
    setSelectedTaxIds(ids => ids.includes(taxId) ? ids.filter(id => id !== taxId) : [...ids, taxId]);
  };

  const submit = async () => {
    if (selectedTaxIds.length === 0) {
      ErrorAlert("Debe seleccionar al menos un impuesto.", "Error de registro", "error");
      return;
    }

    setLoading(true);
    const selectedTaxes = availableTaxes.filter(tax => selectedTaxIds.includes(String(getTaxId(tax))));
    const ok = await onSave({
      id: uid(),
      name: name.trim(),
      categoryId,
      price: isBranchView ? parseFloat(price) : 0,
      emoji,
      icon: emoji,
      file: imageFile,
      imageUrl,
      description: "",
      active: true,
      segments: [],
      taxes: selectedTaxes,
      type: "normal",
      preparation: "yes",
      externalRef: "",
      pointsPay: false,
      terms: "",
      upc: "",
    });
    setLoading(false);
    if (ok) onClose();
  };
  return (
    <Overlay onClose={onClose}>
      <div className="pp-modal-pad--head"><h3 className="pp-modal-title">Nuevo producto</h3></div>
      <div className="pp-modal-pad">
        {/* Image or Emoji */}
        <div className="pp-upload-zone" style={{ marginBottom: 14, height: 100 }} onClick={() => imageInputRef.current?.click()}>
          {useImage && imageUrl ? (
            <img src={imageUrl} alt="" className="pp-upload-preview" />
          ) : (
            <><span className="pp-upload-emoji-md">{emoji}</span><div className="pp-upload-hint"><I.Image /> Subir imagen (opcional)</div></>
          )}
        </div>
        <input ref={imageInputRef} hidden type="file" accept="image/*" onChange={e => pickImage(e.target.files?.[0])} />
        <div className="pp-edit-emoji"><label className="pp-form-label">Ícono</label><EmojiPicker emojis={EMOJIS_PROD} selected={emoji} onSelect={setEmoji} /></div>
        <FG label="Nombre"><input className="pp-form-input" placeholder="Ej: Hamburguesa clásica" value={name} onChange={e => setName(e.target.value)} autoFocus /></FG>
        <div style={{ display: "grid", gridTemplateColumns: isBranchView ? "1fr 1fr" : "1fr", gap: 12 }}>
          <FG label="Categoría"><select className="pp-form-select" value={categoryId} onChange={e => setCategoryId(e.target.value)}>{categories.map(c => <option key={c.id} value={c.id}>{c.emoji} {c.name}</option>)}</select></FG>
          {isBranchView && <FG label="Precio"><input className="pp-form-input" placeholder="0.00" type="number" step="0.01" value={price} onChange={e => setPrice(e.target.value)} /></FG>}
        </div>
        <FG label="Impuestos">
          {taxesLoading ? (
            <div className="pp-tax-empty">Cargando impuestos...</div>
          ) : availableTaxes.length === 0 ? (
            <div className="pp-tax-empty pp-danger">No hay impuestos disponibles.</div>
          ) : (
            <div className="pp-tax-choice-list">
              {availableTaxes.map(tax => {
                const taxId = String(getTaxId(tax));
                const selected = selectedTaxIds.includes(taxId);
                return (
                  <button key={taxId} type="button" className={cx("pp-tax-choice", selected && "pp-tax-choice--active")} onClick={() => toggleTax(taxId)}>
                    <span>{tax.name}</span>
                    <span className={cx("pp-tax-choice-rate", selected && "pp-tax-choice-rate--active")}>{tax.rate}</span>
                  </button>
                );
              })}
            </div>
          )}
        </FG>
      </div>
      <div className="pp-modal-footer"><Btn t="s" onClick={onClose} disabled={loading}>Cancelar</Btn><Btn t="p" disabled={loading || taxesLoading || !name.trim() || !categoryId || selectedTaxIds.length === 0 || (isBranchView && !price)}
        onClick={submit}>{loading ? "Creando..." : "Crear"}</Btn></div>
    </Overlay>
  );
}

function BranchAssignModal({ businessId, branchId, currentProducts, onClose, onAssigned }) {
  const [availableProducts, setAvailableProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const token = localStorage.getItem("x-access-token");

  useEffect(() => {
    const loadAvailable = async () => {
      try {
        setLoading(true);
        const currentIds = new Set(currentProducts.map(product => String(getGroupId(product))));
        const response = await GroupsFechtAPI.getGroupsByBusiness(businessId, token);
        const productsToAssign = asArray(response?.data?.data)
          .filter(product => product?.type === "normal" || product?.type === "reservable")
          .map(normalizeProduct)
          .filter(product => !currentIds.has(String(getGroupId(product))));
        setAvailableProducts(productsToAssign);
      } catch (err) {
        console.log(err);
        setAvailableProducts([]);
        ErrorAlert(getApiError(err, "No se pudieron cargar los productos."), "Error de productos", "error");
      } finally {
        setLoading(false);
      }
    };

    loadAvailable();
  }, [businessId, currentProducts, token]);

  const filteredProducts = availableProducts.filter(product => product.name.toLowerCase().includes(search.toLowerCase()));

  const assignProduct = async (product) => {
    try {
      setLoading(true);
      await GroupsFechtAPI.asingGroupToBranchoffice(branchId, { group_id: getGroupId(product) }, token);
      ErrorAlert("", "Producto asignado con éxito", "success");
      onAssigned?.();
      onClose();
    } catch (err) {
      console.log(err);
      ErrorAlert(getApiError(err, "No se pudo asignar el producto."), "Error de asignación", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay onClose={onClose}>
      <div className="pp-modal-pad--head"><h3 className="pp-modal-title">Asignar producto a sucursal</h3></div>
      <div className="pp-modal-pad--scroll">
        <input className="pp-form-input" style={{ marginBottom: 12 }} placeholder="Buscar producto..." value={search} onChange={e => setSearch(e.target.value)} autoFocus />
        {loading && <div style={{ padding: 20, textAlign: "center", color: "#9090A0", fontSize: 12 }}>Cargando productos...</div>}
        {!loading && filteredProducts.length === 0 && <div style={{ padding: 20, textAlign: "center", color: "#9090A0", fontSize: 12 }}>No hay productos disponibles para asignar.</div>}
        {!loading && filteredProducts.map(product => (
          <button key={product.id} className="pp-assign-row" onClick={() => assignProduct(product)}>
            <div className="pp-assign-row-thumb">
              {product.imageUrl ? <img src={product.imageUrl} alt="" /> : <span className="pp-assign-row-emoji">{product.emoji}</span>}
            </div>
            <div className="pp-assign-row-body">
              <div className="pp-assign-row-name">{product.name}</div>
              <div className="pp-assign-row-price">{fmt(product.price)}</div>
            </div>
            <span className="pp-assign-row-cta">Asignar</span>
          </button>
        ))}
      </div>
      <div className="pp-modal-footer"><Btn t="s" onClick={onClose} disabled={loading}>Cerrar</Btn></div>
    </Overlay>
  );
}

function ImportModal({ onClose, onImported }) {
  const [drag, setDrag] = useState(false);
  const [productsFile, setProductsFile] = useState(null);
  const [optionalsFile, setOptionalsFile] = useState(null);
  const [taxId, setTaxId] = useState("");
  const [taxes, setTaxes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allBranches, setAllBranches] = useState(true);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const productsInputRef = useRef(null);
  const optionalsInputRef = useRef(null);

  const token = localStorage.getItem("x-access-token");
  const user = getStoredUser();
  const businessId = getBusinessId(user);
  const branchoffices = asArray(user?.branchoffices);

  useEffect(() => {
    const getTaxes = async () => {
      try {
        const response = await TaxesFetchAPI.getTaxes(token);
        setTaxes(asArray(response?.data?.data));
      } catch (err) {
        console.log(err);
        setTaxes([]);
      }
    };

    getTaxes();
  }, [token]);

  const pickProductsFile = (files) => {
    const file = files?.[0];
    if (file) {
      setProductsFile(file);
    }
  };

  const pickOptionalsFile = (files) => {
    const file = files?.[0];
    if (file) {
      setOptionalsFile(file);
    }
  };

  const toggleBranch = (branchId) => {
    setAllBranches(false);
    setSelectedBranches(prev => prev.includes(branchId)
      ? prev.filter(id => id !== branchId)
      : [...prev, branchId]
    );
  };

  const downloadProductsTemplate = async () => {
    const ok = await ExcelModuleFechtAPI.DownloadTemplateGroups(token);
    if (!ok) {
      ErrorAlert("No se pudo descargar el template de productos", "Error de descarga", "error");
    }
  };

  const downloadOptionalsTemplate = async () => {
    const ok = await ExcelModuleFechtAPI.DownloadTemplateOptionals(token);
    if (!ok) {
      ErrorAlert("No se pudo descargar el template de modificadores", "Error de descarga", "error");
    }
  };

  const submit = async () => {
    if (!productsFile) {
      return ErrorAlert("", "Debes subir el Excel de productos", "warning");
    }

    if (!taxId) {
      return ErrorAlert("", "Debes seleccionar un impuesto", "warning");
    }

    if (!allBranches && selectedBranches.length === 0) {
      return ErrorAlert("", "Debes seleccionar al menos una sucursal o aplicar a todas", "warning");
    }

    const formData = new FormData();
    formData.append("products_file", productsFile);
    if (optionalsFile) {
      formData.append("optionals_file", optionalsFile);
    }
    formData.append("tax_id", taxId);
    formData.append("apply_all_branchoffices", allBranches ? "1" : "0");

    if (!allBranches) {
      formData.append("branchoffice_ids", JSON.stringify(selectedBranches));
    }

    try {
      setLoading(true);
      await ExcelModuleFechtAPI.UploadBusinessGroup(businessId, formData, token);
      ErrorAlert("", "Menú importado con éxito", "success");
      onImported?.();
      onClose();
    } catch (err) {
      ErrorAlert(
        err?.response?.data?.message || err?.response?.data?.error || "Error al importar el menú",
        "Error al importar el menú",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay onClose={onClose}>
      <div className="pp-modal-pad--head"><h3 className="pp-modal-title">Importar desde Excel</h3></div>
      <div className="pp-modal-pad--scroll">
        <div className="pp-import-templates">
          <Btn t="s" onClick={downloadProductsTemplate} style={{ fontSize: 12, padding: "6px 12px" }}>Plantilla productos</Btn>
          <Btn t="s" onClick={downloadOptionalsTemplate} style={{ fontSize: 12, padding: "6px 12px" }}>Plantilla modificadores</Btn>
        </div>
        <div className="pp-upload-zone pp-import-drop" style={{ borderColor: drag ? "#1A1A2E" : "#D0D0D8" }}
          onClick={() => productsInputRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={e => { e.preventDefault(); setDrag(false); pickProductsFile(e.dataTransfer.files); }}>
          <I.FileSpreadsheet />
          <span className="pp-import-drop-name">{productsFile ? productsFile.name : "Arrastra tu archivo de productos aquí"}</span>
          <span className="pp-import-drop-hint">.xlsx, .xls</span>
        </div>

        <input ref={productsInputRef} hidden type="file" accept=".xlsx,.xls" onChange={e => pickProductsFile(e.target.files)} />

        <div className="pp-import-block">
          <label className="pp-form-label">Archivo de modificadores opcional</label>
          <div className="pp-import-file-row">
            <Btn t="s" onClick={() => optionalsInputRef.current?.click()} style={{ fontSize: 12, padding: "6px 12px" }}>Seleccionar archivo</Btn>
            <span className="pp-import-file-name">{optionalsFile ? optionalsFile.name : "No seleccionado"}</span>
          </div>
          <input ref={optionalsInputRef} hidden type="file" accept=".xlsx,.xls" onChange={e => pickOptionalsFile(e.target.files)} />
        </div>

        <div className="pp-import-block--lg">
          <label className="pp-form-label">Sucursales</label>
          <div className="pp-import-branches">
            <label className="pp-import-branch-all">
              <input type="checkbox" checked={allBranches} onChange={e => { setAllBranches(e.target.checked); if (e.target.checked) setSelectedBranches([]); }} />
              Aplicar a todas las sucursales
            </label>
            {branchoffices.map((branch, index) => {
              const branchId = branch.branchoffice_id || branch.id;
              return (
                <label key={branchId || index} className="pp-import-branch">
                  <input type="checkbox" checked={!allBranches && selectedBranches.includes(branchId)} onChange={() => toggleBranch(branchId)} />
                  {branch.branchoffice_name || branch.name || "Sucursal"}
                </label>
              );
            })}
          </div>
        </div>

        <div className="pp-import-block--lg">
          <label className="pp-form-label">Impuesto</label>
          <select className="pp-form-select" value={taxId} onChange={e => setTaxId(e.target.value)}>
            <option value="">Seleccione un impuesto</option>
            {taxes.map(tax => (
              <option key={tax.id} value={tax.id}>{tax.tax} - {tax.percentage}</option>
            ))}
          </select>
        </div>

        <div className="pp-import-note">
          <div className="pp-import-note-title">Formato:</div>
          <div className="pp-import-note-desc">Productos: categorías, productos, precios y SKU. Modificadores: opcional, conectado por SKU Producto.</div>
        </div>
      </div>
      <div className="pp-modal-footer"><Btn t="s" onClick={onClose} disabled={loading}>Cancelar</Btn><Btn t="p" disabled={loading} onClick={submit}><I.Upload /> {loading ? "Importando..." : "Procesar"}</Btn></div>
    </Overlay>
  );
}